/**
 * API Client for Fias Platform
 */

import { authApi } from './auth-api';
import { traceApiRequest, traceApiResponse, traceApiError } from './telemetry';
import { maintenanceState } from './maintenance';

// Use VITE_API_URL for production (separate API subdomain)
// Fall back to /v1 for local development (proxied by Vite)
const API_BASE = import.meta.env.VITE_API_URL || '/v1';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

class ApiClient {
  private async request<T>(
    method: string,
    path: string,
    body?: unknown,
    options?: { signal?: AbortSignal },
  ): Promise<T> {
    const url = `${API_BASE}${path}`;
    const traceId = traceApiRequest(method, url, body);

    const headers: Record<string, string> = {};

    // Only set Content-Type if there's a body to send
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }

    // Get valid ID token from authApi (auto-refreshes if expired)
    const authToken = await authApi.getValidIdToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    let response: Response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
        signal: options?.signal,
      });
    } catch (err) {
      traceApiError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }

    // Handle 204 No Content responses (e.g., DELETE operations)
    if (response.status === 204) {
      traceApiResponse(traceId, response.status, null);
      return undefined as T;
    }

    let data: unknown;
    let responseText: string | undefined;
    try {
      // Clone response to read text if JSON parsing fails
      const responseClone = response.clone();
      try {
        data = await response.json();
      } catch {
        // JSON parsing failed - get the raw text for debugging
        responseText = await responseClone.text();
        const requestSize = body ? JSON.stringify(body).length : 0;
        traceApiError(traceId, `Failed to parse JSON response (status: ${response.status})`, {
          status: response.status,
          responseText: responseText,
          responseSize: responseText.length,
          requestSize,
        });
        throw new Error(`Failed to parse response (status: ${response.status})`);
      }
    } catch (parseError) {
      if (parseError instanceof Error && parseError.message.startsWith('Failed to parse')) {
        throw parseError;
      }
      // Unexpected error during response handling
      traceApiError(
        traceId,
        parseError instanceof Error ? parseError : new Error(String(parseError)),
      );
      throw parseError;
    }

    traceApiResponse(traceId, response.status, data);

    if (!response.ok) {
      // Handle various error formats:
      // - {error: {code: ..., message: ...}} from Lambda
      // - {error: "..."} from some APIs
      // - {message: "..."} from API Gateway
      const errorData = data as {
        error?: string | { code?: string; message?: string };
        message?: string;
      };
      let errorMessage = 'Request failed';
      let errorCode: string | undefined;
      if (typeof errorData.error === 'object' && errorData.error?.message) {
        errorMessage = errorData.error.message;
        errorCode = errorData.error.code;
      } else if (typeof errorData.error === 'string') {
        errorMessage = errorData.error;
      } else if (errorData.message) {
        errorMessage = errorData.message;
      }

      // Detect maintenance mode — set global state so the UI can react
      if (response.status === 503 && errorCode === 'MAINTENANCE_MODE') {
        maintenanceState.setActive(errorMessage);
      }

      throw new ApiError(errorMessage, response.status, errorCode);
    }

    return data as T;
  }

  /**
   * Generic GET request with optional query parameters
   */
  async get<T>(path: string, options?: { params?: Record<string, string> }): Promise<{ data: T }> {
    let url = path;
    if (options?.params) {
      const params = new URLSearchParams(options.params);
      url = `${path}?${params.toString()}`;
    }
    const data = await this.request<T>('GET', url);
    return { data };
  }

  // Arche API
  async listArches(options?: { categoryId?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ arches: unknown[]; total: number }>(
      'GET',
      `/arches${query ? `?${query}` : ''}`,
    );
  }

  async getArche(id: string) {
    return this.request<{ arche: unknown }>('GET', `/arches/${id}`);
  }

  /**
   * Gets lightweight arche metadata for route protection
   * Used to determine if an arche is public/fullscreen before loading full content
   */
  async getArcheMetadata(id: string) {
    return this.request<ArcheMetadata>('GET', `/arches/${id}/metadata`);
  }

  // Arche File Operations API

  /**
   * Write or manipulate a file in an arche's sandboxed folder
   */
  async archeFileWrite(
    archeId: string,
    payload: {
      filename: string;
      content: string;
      format: string;
      mode: string;
      separator?: string;
      section?: string;
      find?: string;
      requestId?: string;
    },
  ) {
    return this.request<{
      success: boolean;
      fileId: string;
      filename: string;
    }>('POST', `/arches/${archeId}/files/write`, payload);
  }

  /**
   * Read a file from an arche's sandboxed folder
   */
  async archeFileRead(archeId: string, options: { filename?: string; fileId?: string }) {
    const params = new URLSearchParams();
    if (options.filename) params.set('filename', options.filename);
    if (options.fileId) params.set('fileId', options.fileId);
    const query = params.toString();
    return this.request<{
      content: string;
      filename: string;
      fileId: string;
    }>('GET', `/arches/${archeId}/files/read${query ? `?${query}` : ''}`);
  }

  /**
   * Get a presigned download URL for a file in an arche's sandboxed folder
   */
  async archeFileDownloadUrl(archeId: string, options: { filename?: string; fileId?: string }) {
    const params = new URLSearchParams();
    if (options.filename) params.set('filename', options.filename);
    if (options.fileId) params.set('fileId', options.fileId);
    const query = params.toString();
    return this.request<{
      downloadUrl: string;
      filename: string;
    }>('GET', `/arches/${archeId}/files/download-url${query ? `?${query}` : ''}`);
  }

  /**
   * List files in an arche's sandboxed folder
   */
  async archeFileList(archeId: string) {
    return this.request<{
      files: Array<{ fileId: string; filename: string; format: string; createdAt: string }>;
    }>('GET', `/arches/${archeId}/files`);
  }

  // Entity API
  async listEntities(options?: { categoryId?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ entities: unknown[]; total: number }>(
      'GET',
      `/entities${query ? `?${query}` : ''}`,
    );
  }

  async getEntity(id: string) {
    return this.request<{ entity: unknown }>('GET', `/entities/${id}`);
  }

  async invokeEntity(entityId: string, input: Record<string, unknown>) {
    return this.request<{
      output: unknown;
      usage?: {
        tokens?: { input: number; output: number };
        cost?: number;
      };
    }>('POST', `/entities/${entityId}/invoke`, { input });
  }

  // Category API
  async listCategories() {
    return this.request<{ categories: unknown[] }>('GET', '/categories');
  }

  async getCategory(id: string) {
    return this.request<{ category: unknown }>('GET', `/categories/${id}`);
  }

  // Workflow API
  async startArche(archeId: string, input?: Record<string, unknown>) {
    return this.request<{
      requestId: string;
      archId: string;
      status: 'pending' | 'running';
    }>('POST', `/arches/${archeId}/start`, { input });
  }

  async getRequestStatus(requestId: string) {
    return this.request<{
      requestId: string;
      archId: string;
      userId: string;
      status: 'pending' | 'running' | 'completed' | 'failed' | 'cancelled' | 'timed_out';
      input: Record<string, unknown> | null;
      output: Record<string, unknown> | null;
      startedAt: string;
      completedAt: string | null;
      totalCostUsd: number | null;
      errorMessage: string | null;
      nodes: Array<{
        nodeId: string;
        entityId: string | null;
        entityName: string | null;
        status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped';
        input: Record<string, unknown> | null;
        output: Record<string, unknown> | null;
        startedAt: string | null;
        completedAt: string | null;
        costUsd: number | null;
        latencyMs: number | null;
        errorMessage: string | null;
      }>;
    }>('GET', `/requests/${requestId}`);
  }

  async listRequests(options?: {
    archId?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archId) params.set('archId', options.archId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      requests: Array<{
        requestId: string;
        archId: string;
        status: string;
        startedAt: string;
        completedAt: string | null;
      }>;
      total: number;
    }>('GET', `/requests${query ? `?${query}` : ''}`);
  }

  // ==========================================================================
  // Favorites API
  // ==========================================================================

  async getFavorites() {
    return this.request<{ favorites: UserFavoriteWithArche[] }>('GET', '/arches/favorites');
  }

  async addFavorite(archId: string) {
    return this.request<{ favorite: unknown }>('POST', '/arches/favorites', { archId });
  }

  async removeFavorite(archId: string) {
    return this.request<void>('DELETE', `/arches/favorites/${archId}`);
  }

  async checkFavorite(archId: string) {
    return this.request<{ isFavorite: boolean }>('GET', `/arches/favorites/check/${archId}`);
  }

  async reorderFavorites(placements: { archId: string; position: number }[]) {
    return this.request<{ success: boolean }>('PUT', '/arches/favorites/reorder', { placements });
  }

  // ==========================================================================
  // Marketplace API
  // ==========================================================================

  async searchMarketplace(options?: {
    q?: string;
    categoryId?: string;
    archeType?: ArcheType;
    tags?: string[];
    sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.q) params.set('q', options.q);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.archeType) params.set('archeType', options.archeType);
    if (options?.tags?.length) params.set('tags', options.tags.join(','));
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<MarketplaceSearchResult>(
      'GET',
      `/marketplace/search${query ? `?${query}` : ''}`,
    );
  }

  async getFeaturedArches(limit?: number) {
    const query = limit ? `?limit=${limit}` : '';
    return this.request<{ arches: MarketplaceArche[] }>('GET', `/marketplace/featured${query}`);
  }

  async getMarketplaceCategories() {
    return this.request<{ categories: MarketplaceCategory[] }>('GET', '/marketplace/categories');
  }

  async getMarketplaceCategoryArches(
    slug: string,
    options?: {
      sortBy?: 'fitness' | 'popular' | 'newest';
      limit?: number;
      offset?: number;
    },
  ) {
    const params = new URLSearchParams();
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      category: MarketplaceCategory;
      arches: MarketplaceArche[];
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    }>('GET', `/marketplace/categories/${slug}${query ? `?${query}` : ''}`);
  }

  async getMarketplaceArche(id: string) {
    return this.request<MarketplaceArcheDetail>('GET', `/marketplace/arches/${id}`);
  }

  // ==========================================================================
  // Marketplace Entity API
  // ==========================================================================

  async searchMarketplaceEntities(options?: {
    q?: string;
    categoryId?: string;
    type?: EntityType;
    sortBy?: 'relevance' | 'fitness' | 'popular' | 'newest';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.q) params.set('q', options.q);
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.type) params.set('type', options.type);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<MarketplaceEntitySearchResult>(
      'GET',
      `/marketplace/entities/search${query ? `?${query}` : ''}`,
    );
  }

  async getFeaturedEntities(limit?: number) {
    const query = limit ? `?limit=${limit}` : '';
    return this.request<{ entities: MarketplaceEntity[] }>(
      'GET',
      `/marketplace/entities/featured${query}`,
    );
  }

  async getMarketplaceEntity(id: string) {
    return this.request<MarketplaceEntityDetail>('GET', `/marketplace/entities/${id}`);
  }

  async getMarketplaceCategoriesWithEntityCounts() {
    return this.request<{ categories: MarketplaceCategoryWithEntityCount[] }>(
      'GET',
      '/marketplace/entity-categories',
    );
  }

  // ==========================================================================
  // Contributor API
  // ==========================================================================

  async getContributorMetrics() {
    return this.request<ContributorMetricsResult>('GET', '/contributor/entities/metrics');
  }

  async getContributorArcheDashboard() {
    return this.request<ContributorArcheDashboardResult>('GET', '/contributor/arches/dashboard');
  }

  // ==========================================================================
  // Credits API
  // ==========================================================================

  async getCreditBalance() {
    return this.request<CreditBalance>('GET', '/credits');
  }

  async getCreditSpendingSummary() {
    return this.request<{ categories: SpendingCategorySummary[] }>('GET', '/credits/summary');
  }

  async getCreditTransactions(options?: {
    type?: 'grant' | 'usage' | 'refund' | 'adjustment';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<CreditTransactionsResult>(
      'GET',
      `/credits/transactions${query ? `?${query}` : ''}`,
    );
  }

  async grantCredits(targetUserId: string, amount: number, description?: string) {
    return this.request<CreditGrantResult>('POST', '/admin/credits/grant', {
      userId: targetUserId,
      amount,
      description,
    });
  }

  // ==========================================================================
  // Payments API
  // ==========================================================================

  async getPaymentConfig() {
    return this.request<PaymentConfig>('GET', '/payments/config');
  }

  async createCheckoutSession(amountCents: number) {
    return this.request<CheckoutSessionResult>('POST', '/payments/checkout-session', {
      amountCents,
    });
  }

  async createCryptoCheckout(amountCents: number) {
    return this.request<CryptoCheckoutResult>('POST', '/payments/crypto/checkout', {
      amountCents,
    });
  }

  // ==========================================================================
  // Subscriptions API
  // ==========================================================================

  async getSubscriptionTiers() {
    return this.request<SubscriptionTiersResult>('GET', '/payments/subscriptions/tiers');
  }

  async getCurrentSubscription() {
    return this.request<SubscriptionInfo>('GET', '/payments/subscriptions/current');
  }

  async subscribeToPremium() {
    return this.request<{ sessionUrl: string }>('POST', '/payments/subscriptions/subscribe');
  }

  async cancelSubscription() {
    return this.request<{
      status: string;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }>('POST', '/payments/subscriptions/cancel');
  }

  async reactivateSubscription() {
    return this.request<{
      status: string;
      cancelAtPeriodEnd: boolean;
      currentPeriodEnd: string | null;
    }>('POST', '/payments/subscriptions/reactivate');
  }

  async getSubscriptionPortalUrl() {
    return this.request<{ url: string }>('GET', '/payments/subscriptions/portal');
  }

  // ==========================================================================
  // Admin Payments API
  // ==========================================================================

  // Maintenance Mode
  async adminGetMaintenanceStatus() {
    return this.request<MaintenanceStatus>('GET', '/admin/maintenance');
  }

  async adminSetMaintenanceMode(params: { enabled: boolean; message?: string }) {
    return this.request<MaintenanceStatus>('POST', '/admin/maintenance', params);
  }

  async adminGetPaymentsDashboard(period?: string) {
    return this.request<AdminPaymentsDashboard>(
      'GET',
      `/admin/payments/dashboard${period ? `?period=${period}` : ''}`,
    );
  }

  async adminGetPaymentsCosts(period?: string) {
    return this.request<AdminPaymentsCosts>(
      'GET',
      `/admin/payments/costs${period ? `?period=${period}` : ''}`,
    );
  }

  async adminProcessRefund(paymentId: string, reason?: string) {
    return this.request<{ success: boolean; paymentId: string }>('POST', '/admin/payments/refund', {
      paymentId,
      reason,
    });
  }

  async adminGrantCredits(userId: string, amount: number, description?: string) {
    return this.request<{ success: boolean; newBalance: number; transactionId: string }>(
      'POST',
      '/admin/payments/grant',
      { userId, amount, description },
    );
  }

  async getPaymentHistory(options?: { limit?: number; offset?: number; status?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.status) params.set('status', options.status);
    const query = params.toString();
    return this.request<PaymentHistoryResult>(
      'GET',
      `/payments/history${query ? `?${query}` : ''}`,
    );
  }

  // ==========================================================================
  // Models API
  // ==========================================================================

  async listModels(options?: {
    provider?: ModelProvider;
    tier?: 'fast' | 'balanced' | 'powerful';
  }) {
    const params = new URLSearchParams();
    if (options?.provider) params.set('provider', options.provider);
    if (options?.tier) params.set('tier', options.tier);
    const query = params.toString();
    return this.request<ModelListResponse>('GET', `/models${query ? `?${query}` : ''}`);
  }

  async getModel(id: string) {
    return this.request<ModelEntity>('GET', `/models/${id}`);
  }

  // ==========================================================================
  // Model Definitions API (Entity-driven parameter schema)
  // ==========================================================================

  /**
   * Lists model definitions with their parameter schemas.
   * Used by the frontend to render data-driven parameter controls.
   *
   * @param options.provider - Filter by provider (openai, anthropic, google)
   * @param options.tier - Filter by tier (fast, balanced, powerful)
   * @param options.modelType - Filter by model type (text, image)
   */
  async listModelDefinitions(options?: {
    provider?: ModelProvider;
    tier?: 'fast' | 'balanced' | 'powerful';
    modelType?: 'text' | 'image';
  }) {
    const params = new URLSearchParams();
    if (options?.provider) params.set('provider', options.provider);
    if (options?.tier) params.set('tier', options.tier);
    if (options?.modelType) params.set('modelType', options.modelType);
    const query = params.toString();
    return this.request<ModelDefinitionListResponse>(
      'GET',
      `/models/definitions${query ? `?${query}` : ''}`,
    );
  }

  /**
   * Gets a model definition by ID, including full parameter schema.
   */
  async getModelDefinition(id: string) {
    return this.request<ModelDefinition>('GET', `/models/definitions/${id}`);
  }

  /**
   * Gets a model definition by provider and model name.
   * Useful when you have the model name but not the entity ID.
   */
  async getModelDefinitionByLookup(provider: ModelProvider, modelName: string) {
    const params = new URLSearchParams();
    params.set('provider', provider);
    params.set('modelName', modelName);
    return this.request<ModelDefinition>('GET', `/models/definitions/lookup?${params.toString()}`);
  }

  // ==========================================================================
  // Conversations API
  // ==========================================================================

  async listConversations(options?: {
    archeId?: string;
    status?: 'active' | 'archived';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.archeId) params.set('archeId', options.archeId);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<ConversationListResult>('GET', `/conversations${query ? `?${query}` : ''}`);
  }

  async getConversation(conversationId: string) {
    return this.request<ConversationWithMessages>('GET', `/conversations/${conversationId}`);
  }

  async createConversation(archeId: string, title?: string) {
    return this.request<ConversationResponse>('POST', '/conversations', { archeId, title });
  }

  async addMessages(conversationId: string, messages: ConversationMessageInput[]) {
    return this.request<{ messages: ConversationMessageResponse[] }>(
      'POST',
      `/conversations/${conversationId}/messages`,
      { messages },
    );
  }

  async addMessage(conversationId: string, message: ConversationMessageInput) {
    return this.request<{ messages: ConversationMessageResponse[] }>(
      'POST',
      `/conversations/${conversationId}/messages`,
      { message },
    );
  }

  async updateConversation(
    conversationId: string,
    updates: { title?: string; status?: 'active' | 'archived' },
  ) {
    return this.request<ConversationResponse>('PATCH', `/conversations/${conversationId}`, updates);
  }

  async deleteConversation(conversationId: string) {
    return this.request<void>('DELETE', `/conversations/${conversationId}`);
  }

  async deleteAllConversations() {
    return this.request<DeleteAllConversationsResult>('DELETE', '/conversations/all');
  }

  async exportConversation(conversationId: string, format: 'json' | 'markdown' = 'markdown') {
    // Export returns plain text (markdown or JSON string), not a JSON object
    // So we need to handle it separately from the standard request method
    const url = `${API_BASE}/conversations/${conversationId}/export?format=${format}`;

    const headers: Record<string, string> = {};
    const authToken = await authApi.getValidIdToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      throw new Error(`Export failed with status ${response.status}`);
    }

    return response.text();
  }

  async shareConversation(conversationId: string) {
    return this.request<ShareConversationResponse>(
      'POST',
      `/conversations/${conversationId}/share`,
      {},
    );
  }

  async getSharedConversation(shareId: string) {
    return this.request<SharedConversationResponse>('GET', `/shared/${shareId}`);
  }

  // ==========================================================================
  // Arche Session API
  // ==========================================================================

  async getArcheSession(archeId: string) {
    return this.request<ArcheSessionResponse>('GET', `/arches/${archeId}/session`);
  }

  async updateArcheSession(archeId: string, updates: ArcheSessionUpdateInput) {
    return this.request<ArcheSessionResponse>('PUT', `/arches/${archeId}/session`, updates);
  }

  async deleteArcheSession(archeId: string) {
    return this.request<void>('DELETE', `/arches/${archeId}/session`);
  }

  // ==========================================================================
  // Fias AI API
  // ==========================================================================

  async fiasAIChat(payload: {
    message: string;
    conversationId?: string;
    mode?: 'hybrid' | 'ai-only' | 'search-only';
    routingPreference?: 'auto' | 'speed' | 'balanced' | 'performance' | 'cost';
    conversationHistory?: Array<{ role: 'user' | 'assistant'; content: string }>;
    archeContext?: {
      archeId: string;
      state: Record<string, unknown>;
      actions: Array<{
        id: string;
        description: string;
        parameterSchema: Record<string, unknown>;
        requiresConfirmation?: boolean;
        category?: string;
      }>;
    };
  }) {
    return this.request<{
      requestId: string;
      conversationId: string;
      selectedModel: {
        provider: string;
        modelName: string;
        displayName: string;
        tier: string;
      };
      searchIncluded: boolean;
    }>('POST', '/arches/fias-ai/chat', payload);
  }

  async listFiasAIConversations(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{
      conversations: Array<{
        conversationId: string;
        title: string | null;
        messageCount: number;
        createdAt: string;
        updatedAt: string;
      }>;
      total: number;
      limit: number;
      offset: number;
      hasMore: boolean;
    }>('GET', `/arches/fias-ai/conversations${query ? `?${query}` : ''}`);
  }

  async getFiasAIConversation(id: string) {
    return this.request<{
      conversationId: string;
      title: string | null;
      messages: Array<{
        messageId: string;
        role: 'user' | 'assistant';
        content: string;
        provider: string | null;
        modelName: string | null;
        createdAt: string;
      }>;
    }>('GET', `/arches/fias-ai/conversations/${id}`);
  }

  async deleteFiasAIConversation(id: string) {
    return this.request<void>('DELETE', `/arches/fias-ai/conversations/${id}`);
  }

  // ==========================================================================
  // Contact Book API
  // ==========================================================================

  async listContacts(options?: {
    categoryId?: string;
    priorityId?: string;
    search?: string;
    dateFrom?: string;
    dateTo?: string;
    status?: 'active' | 'archived' | 'deleted';
    sortBy?: 'name' | 'gatheredAt' | 'company' | 'createdAt';
    sortDir?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.categoryId) params.set('categoryId', options.categoryId);
    if (options?.priorityId) params.set('priorityId', options.priorityId);
    if (options?.search) params.set('search', options.search);
    if (options?.dateFrom) params.set('dateFrom', options.dateFrom);
    if (options?.dateTo) params.set('dateTo', options.dateTo);
    if (options?.status) params.set('status', options.status);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortDir) params.set('sortDir', options.sortDir);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ contacts: ContactDetail[]; total: number }>(
      'GET',
      `/arches/contact-book/contacts${query ? `?${query}` : ''}`,
    );
  }

  async getContact(id: string) {
    return this.request<ContactDetail>('GET', `/arches/contact-book/contacts/${id}`);
  }

  async createContact(input: ContactInput) {
    return this.request<ContactDetail>('POST', '/arches/contact-book/contacts', input);
  }

  async updateContact(id: string, input: Partial<ContactInput>) {
    return this.request<ContactDetail>('PUT', `/arches/contact-book/contacts/${id}`, input);
  }

  async deleteContact(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/contacts/${id}`);
  }

  async archiveContact(id: string, archived: boolean) {
    return this.request<ContactDetail>('PATCH', `/arches/contact-book/contacts/${id}/archive`, {
      archived,
    });
  }

  async restoreContact(id: string) {
    return this.request<ContactDetail>('PATCH', `/arches/contact-book/contacts/${id}/restore`);
  }

  async permanentlyDeleteContact(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/contacts/${id}/permanent`);
  }

  async emptyTrash() {
    return this.request<{ deletedCount: number }>('DELETE', '/arches/contact-book/trash');
  }

  async getTrashCount() {
    return this.request<{ count: number }>('GET', '/arches/contact-book/trash/count');
  }

  async listContactCategories() {
    return this.request<{ categories: ContactCategory[] }>(
      'GET',
      '/arches/contact-book/categories',
    );
  }

  async createContactCategory(input: ContactCategoryInput) {
    return this.request<ContactCategory>('POST', '/arches/contact-book/categories', input);
  }

  async updateContactCategory(id: string, input: Partial<ContactCategoryInput>) {
    return this.request<ContactCategory>('PUT', `/arches/contact-book/categories/${id}`, input);
  }

  async deleteContactCategory(id: string) {
    return this.request<{ uncategorizedCount: number }>(
      'DELETE',
      `/arches/contact-book/categories/${id}`,
    );
  }

  async reorderContactCategories(categoryIds: string[]) {
    return this.request<{ categories: ContactCategory[] }>(
      'PUT',
      '/arches/contact-book/categories/reorder',
      { categoryIds },
    );
  }

  async listContactPriorities() {
    return this.request<{ priorities: ContactPriority[] }>(
      'GET',
      '/arches/contact-book/priorities',
    );
  }

  async createContactPriority(input: ContactPriorityInput) {
    return this.request<ContactPriority>('POST', '/arches/contact-book/priorities', input);
  }

  async updateContactPriority(id: string, input: Partial<ContactPriorityInput>) {
    return this.request<ContactPriority>('PUT', `/arches/contact-book/priorities/${id}`, input);
  }

  async deleteContactPriority(id: string) {
    return this.request<void>('DELETE', `/arches/contact-book/priorities/${id}`);
  }

  async listContactAttachments(contactId: string) {
    return this.request<{ attachments: ContactAttachment[] }>(
      'GET',
      `/arches/contact-book/contacts/${contactId}/attachments`,
    );
  }

  async addContactAttachment(contactId: string, fileId: string, label?: string) {
    return this.request<ContactAttachment>(
      'POST',
      `/arches/contact-book/contacts/${contactId}/attachments`,
      { fileId, label },
    );
  }

  async removeContactAttachment(contactId: string, attachmentId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/contact-book/contacts/${contactId}/attachments/${attachmentId}`,
    );
  }

  // ==========================================================================
  // Calendar API
  // ==========================================================================

  async listCalendarCollections() {
    return this.request<{ calendars: CalendarCollectionDetail[] }>(
      'GET',
      '/arches/calendar/calendars',
    );
  }

  async createCalendarCollection(input: CalendarCollectionInput) {
    return this.request<CalendarCollectionDetail>('POST', '/arches/calendar/calendars', input);
  }

  async updateCalendarCollection(id: string, input: Partial<CalendarCollectionInput>) {
    return this.request<CalendarCollectionDetail>('PUT', `/arches/calendar/calendars/${id}`, input);
  }

  async deleteCalendarCollection(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/calendars/${id}`);
  }

  async listCalendarEvents(options: {
    rangeStart: string;
    rangeEnd: string;
    calendarId?: string;
    search?: string;
    status?: string;
  }) {
    const params = new URLSearchParams();
    params.set('rangeStart', options.rangeStart);
    params.set('rangeEnd', options.rangeEnd);
    if (options.calendarId) params.set('calendarId', options.calendarId);
    if (options.search) params.set('search', options.search);
    if (options.status) params.set('status', options.status);
    return this.request<{ events: CalendarEventInstance[] }>(
      'GET',
      `/arches/calendar/events?${params.toString()}`,
    );
  }

  async getCalendarEvent(id: string) {
    return this.request<CalendarEventDetail>('GET', `/arches/calendar/events/${id}`);
  }

  async createCalendarEvent(input: CalendarEventCreateInput) {
    return this.request<CalendarEventDetail>('POST', '/arches/calendar/events', input);
  }

  async updateCalendarEvent(
    id: string,
    input: Partial<CalendarEventCreateInput> & {
      scope?: 'this' | 'this_and_future' | 'all';
      instanceDate?: string;
    },
  ) {
    return this.request<CalendarEventDetail>('PUT', `/arches/calendar/events/${id}`, input);
  }

  async deleteCalendarEvent(
    id: string,
    scope?: 'this' | 'this_and_future' | 'all',
    instanceDate?: string,
  ) {
    const params = new URLSearchParams();
    if (scope) params.set('scope', scope);
    if (instanceDate) params.set('instanceDate', instanceDate);
    const query = params.toString();
    return this.request<void>('DELETE', `/arches/calendar/events/${id}${query ? `?${query}` : ''}`);
  }

  async parseCalendarImport(file: File): Promise<CalendarParsedImportResult> {
    const content = await file.text();
    return this.request<CalendarParsedImportResult>('POST', '/arches/calendar/import/parse', {
      fileName: file.name,
      content,
    });
  }

  async confirmCalendarImport(
    request: CalendarImportConfirmRequest,
  ): Promise<CalendarImportConfirmResult> {
    return this.request<CalendarImportConfirmResult>(
      'POST',
      '/arches/calendar/import/confirm',
      request,
    );
  }

  async parseNaturalEvent(input: {
    text: string;
    timezone?: string;
    referenceDate?: string;
  }): Promise<ParsedEventFields> {
    return this.request<ParsedEventFields>('POST', '/arches/calendar/events/parse-natural', input);
  }

  async exportCalendar(calendarId: string): Promise<string> {
    const url = `${API_BASE}/arches/calendar/export/${calendarId}`;
    const traceId = traceApiRequest('GET', url);
    const headers: Record<string, string> = {};
    const authToken = await authApi.getValidIdToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    let response: Response;
    try {
      response = await fetch(url, { headers });
    } catch (err) {
      traceApiError(traceId, err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
    if (!response.ok) {
      const text = await response.text();
      const error = new Error(text || `Export failed with status ${response.status}`);
      traceApiError(traceId, error);
      throw error;
    }
    const text = await response.text();
    traceApiResponse(traceId, response.status, { length: text.length });
    return text;
  }

  // ==========================================================================
  // Calendar Event Templates API
  // ==========================================================================

  async listCalendarEventTemplates() {
    return this.request<{ templates: CalendarEventTemplateDetail[] }>(
      'GET',
      '/arches/calendar/templates',
    );
  }

  async createCalendarEventTemplate(input: CalendarEventTemplateInput) {
    return this.request<CalendarEventTemplateDetail>('POST', '/arches/calendar/templates', input);
  }

  async updateCalendarEventTemplate(id: string, input: Partial<CalendarEventTemplateInput>) {
    return this.request<CalendarEventTemplateDetail>(
      'PUT',
      `/arches/calendar/templates/${id}`,
      input,
    );
  }

  async deleteCalendarEventTemplate(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/templates/${id}`);
  }

  // ==========================================================================
  // Calendar Sharing API
  // ==========================================================================

  async searchCalendarUsers(username: string) {
    return this.request<{ users: UserSearchResult[] }>(
      'GET',
      `/arches/calendar/users/search?username=${encodeURIComponent(username)}`,
    );
  }

  async listCalendarShares(calendarId: string) {
    return this.request<{ shares: CalendarShareDetail[] }>(
      'GET',
      `/arches/calendar/calendars/${calendarId}/shares`,
    );
  }

  async createCalendarShare(calendarId: string, input: CalendarShareInput) {
    return this.request<CalendarShareDetail>(
      'POST',
      `/arches/calendar/calendars/${calendarId}/shares`,
      input,
    );
  }

  async updateCalendarShare(calendarId: string, shareId: string, input: CalendarShareUpdate) {
    return this.request<CalendarShareDetail>(
      'PUT',
      `/arches/calendar/calendars/${calendarId}/shares/${shareId}`,
      input,
    );
  }

  async deleteCalendarShare(calendarId: string, shareId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/calendar/calendars/${calendarId}/shares/${shareId}`,
    );
  }

  async listSharedCalendars() {
    return this.request<{ sharedCalendars: SharedCalendarSummary[] }>(
      'GET',
      '/arches/calendar/shared-calendars',
    );
  }

  async listSharedCalendarEvents(calendarId: string, rangeStart: string, rangeEnd: string) {
    const params = new URLSearchParams({ rangeStart, rangeEnd });
    return this.request<{ events: CalendarEventInstance[] }>(
      'GET',
      `/arches/calendar/shared-calendars/${calendarId}/events?${params.toString()}`,
    );
  }

  async findTime(input: FindTimeInput) {
    return this.request<FindTimeResult>('POST', '/arches/calendar/scheduling/find-time', input);
  }

  // ==========================================================================
  // Calendar Scheduling Links API
  // ==========================================================================

  async listSchedulingLinks() {
    return this.request<{ schedulingLinks: SchedulingLinkDetail[] }>(
      'GET',
      '/arches/calendar/scheduling-links',
    );
  }

  async createSchedulingLink(input: SchedulingLinkInput) {
    return this.request<SchedulingLinkDetail>('POST', '/arches/calendar/scheduling-links', input);
  }

  async getSchedulingLink(id: string) {
    return this.request<SchedulingLinkDetail>('GET', `/arches/calendar/scheduling-links/${id}`);
  }

  async updateSchedulingLink(id: string, input: Partial<SchedulingLinkInput>) {
    return this.request<SchedulingLinkDetail>(
      'PUT',
      `/arches/calendar/scheduling-links/${id}`,
      input,
    );
  }

  async deleteSchedulingLink(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/scheduling-links/${id}`);
  }

  async listSchedulingLinkBookings(linkId: string, status?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.request<{ bookings: BookingDetail[] }>(
      'GET',
      `/arches/calendar/scheduling-links/${linkId}/bookings${query ? `?${query}` : ''}`,
    );
  }

  async cancelBookingByHost(linkId: string, bookingId: string) {
    return this.request<void>(
      'DELETE',
      `/arches/calendar/scheduling-links/${linkId}/bookings/${bookingId}`,
    );
  }

  // Public scheduling (no auth required)

  async getPublicSchedulingInfo(slug: string, date?: string, timezone?: string) {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (timezone) params.set('timezone', timezone);
    const query = params.toString();
    return this.request<PublicSchedulingInfo>(
      'GET',
      `/public/scheduling/${slug}${query ? `?${query}` : ''}`,
    );
  }

  async createPublicBooking(
    slug: string,
    input: {
      bookerName: string;
      bookerEmail: string;
      startAt: string;
      notes?: string;
    },
  ) {
    return this.request<BookingConfirmation>('POST', `/public/scheduling/${slug}/book`, input);
  }

  async cancelPublicBooking(slug: string, bookingId: string, cancellationToken: string) {
    return this.request<{ success: boolean }>('POST', `/public/scheduling/${slug}/cancel`, {
      bookingId,
      cancellationToken,
    });
  }

  // ==========================================================================
  // Calendar Schedulable Tasks API
  // ==========================================================================

  async listSchedulableTasks(status?: string) {
    const params = new URLSearchParams();
    if (status) params.set('status', status);
    const query = params.toString();
    return this.request<{ tasks: SchedulableTaskDetail[] }>(
      'GET',
      `/arches/calendar/schedulable-tasks${query ? `?${query}` : ''}`,
    );
  }

  async createSchedulableTask(input: SchedulableTaskInput) {
    return this.request<SchedulableTaskDetail>('POST', '/arches/calendar/schedulable-tasks', input);
  }

  async updateSchedulableTask(id: string, input: Partial<SchedulableTaskInput>) {
    return this.request<SchedulableTaskDetail>(
      'PUT',
      `/arches/calendar/schedulable-tasks/${id}`,
      input,
    );
  }

  async deleteSchedulableTask(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/schedulable-tasks/${id}`);
  }

  async suggestSchedule(
    taskId: string,
    opts?: { workingHoursStart?: number; workingHoursEnd?: number; timezone?: string },
  ) {
    return this.request<{ suggestions: AiScheduleSuggestion[] }>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/suggest`,
      opts || {},
    );
  }

  async acceptScheduleSuggestion(taskId: string, suggestionIndex: number) {
    return this.request<SchedulableTaskDetail>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/accept`,
      { suggestionIndex },
    );
  }

  async completeSchedulableTask(taskId: string) {
    return this.request<SchedulableTaskDetail>(
      'POST',
      `/arches/calendar/schedulable-tasks/${taskId}/complete`,
    );
  }

  // ==========================================================================
  // Calendar Focus Time API
  // ==========================================================================

  async getFocusTimeSettings() {
    return this.request<{ settings: FocusTimeSettingsDetail | null }>(
      'GET',
      '/arches/calendar/focus-time/settings',
    );
  }

  async updateFocusTimeSettings(input: FocusTimeSettingsInput) {
    return this.request<FocusTimeSettingsDetail>(
      'PUT',
      '/arches/calendar/focus-time/settings',
      input,
    );
  }

  async generateFocusTimeBlocks() {
    return this.request<{ blocksCreated: number }>('POST', '/arches/calendar/focus-time/generate');
  }

  async regenerateFocusTimeBlocks() {
    return this.request<{ blocksCreated: number }>(
      'POST',
      '/arches/calendar/focus-time/regenerate',
    );
  }

  async clearFocusTimeBlocks() {
    return this.request<{ deleted: number }>('DELETE', '/arches/calendar/focus-time/blocks');
  }

  // ==========================================================================
  // Calendar Subscriptions API
  // ==========================================================================

  async subscribeToCalendarFeed(input: { feedUrl: string; name?: string }) {
    return this.request<CalendarSubscriptionDetail>(
      'POST',
      '/arches/calendar/subscriptions',
      input,
    );
  }

  async listCalendarSubscriptions() {
    return this.request<{ subscriptions: CalendarSubscriptionDetail[] }>(
      'GET',
      '/arches/calendar/subscriptions',
    );
  }

  async refreshCalendarSubscription(id: string) {
    return this.request<CalendarSubscriptionDetail>(
      'POST',
      `/arches/calendar/subscriptions/${id}/refresh`,
    );
  }

  async unsubscribeCalendar(id: string) {
    return this.request<void>('DELETE', `/arches/calendar/subscriptions/${id}`);
  }

  // Calendar Analytics
  async getCalendarAnalytics(start: string, end: string) {
    const params = new URLSearchParams({ start, end });
    return this.request<CalendarAnalyticsResult>(
      'GET',
      `/arches/calendar/analytics?${params.toString()}`,
    );
  }

  // ==========================================================================
  // Social Media API
  // ==========================================================================

  async listSocialMediaAccounts() {
    return this.request<{ accounts: SocialMediaAccountPublic[] }>(
      'GET',
      '/arches/social-media/accounts',
    );
  }

  async initSocialMediaOAuth(platform: string, redirectUri: string) {
    const params = new URLSearchParams({ platform, redirectUri });
    return this.request<{ authorizationUrl: string; state: string }>(
      'GET',
      `/arches/social-media/accounts/oauth/init?${params.toString()}`,
    );
  }

  async disconnectSocialMediaAccount(accountId: string) {
    return this.request<void>('DELETE', `/arches/social-media/accounts/${accountId}`);
  }

  async listSocialMediaPosts(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ posts: SocialMediaPost[]; total: number }>(
      'GET',
      `/arches/social-media/posts${query ? `?${query}` : ''}`,
    );
  }

  async createSocialMediaPost(data: { originalContent: string; targetAccountIds?: string[] }) {
    return this.request<SocialMediaPost>('POST', '/arches/social-media/posts', data);
  }

  async getSocialMediaPost(postId: string) {
    return this.request<SocialMediaPost>('GET', `/arches/social-media/posts/${postId}`);
  }

  async updateSocialMediaPost(
    postId: string,
    data: {
      originalContent?: string;
      targetAccountIds?: string[];
      publishAt?: string | null;
      platformContent?: Record<string, AdaptedPlatformContent>;
    },
  ) {
    return this.request<SocialMediaPost>('PATCH', `/arches/social-media/posts/${postId}`, data);
  }

  async deleteSocialMediaPost(postId: string) {
    return this.request<void>('DELETE', `/arches/social-media/posts/${postId}`);
  }

  async adaptSocialMediaPost(postId: string, platforms?: string[]) {
    return this.request<{ platformContent: Record<string, AdaptedPlatformContent> }>(
      'POST',
      `/arches/social-media/posts/${postId}/adapt`,
      { platforms },
    );
  }

  async scheduleSocialMediaPost(postId: string, publishAt: string) {
    return this.request<SocialMediaPost>('POST', `/arches/social-media/posts/${postId}/schedule`, {
      publishAt,
    });
  }

  async publishSocialMediaPostNow(postId: string) {
    return this.request<{ results: Record<string, PublishAttemptResult> }>(
      'POST',
      `/arches/social-media/posts/${postId}/publish-now`,
    );
  }

  async getSocialMediaPostLog(postId: string) {
    return this.request<{ log: SocialMediaPublishLogEntry[] }>(
      'GET',
      `/arches/social-media/posts/${postId}/log`,
    );
  }

  async getSocialMediaSuggestions(content: string, platform?: string) {
    return this.request<{ hooks: string[]; engagementTips: string[]; toneAnalysis: string }>(
      'POST',
      '/arches/social-media/ai/suggestions',
      { content, platform },
    );
  }

  async getSocialMediaBestTime(platform: string) {
    return this.request<{
      recommendations: Array<{ dayOfWeek: string; timeRange: string; reason: string }>;
      generalAdvice: string;
    }>('POST', '/arches/social-media/ai/best-time', { platform });
  }

  async getSocialMediaHashtags(content: string, platform: string) {
    return this.request<{ recommended: string[]; trending: string[]; analysis: string }>(
      'POST',
      '/arches/social-media/ai/hashtags',
      { content, platform },
    );
  }

  // ==========================================================================
  // Lit Legion API
  // ==========================================================================

  async listLitLegionTasks(options?: {
    category?: string;
    status?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitLegionTaskStatusListResult>(
      'GET',
      `/arches/lit-legion/tasks${qs ? `?${qs}` : ''}`,
    );
  }

  async getLitLegionTask(taskId: string) {
    return this.request<{ task: LitLegionTaskWithStatus }>(
      'GET',
      `/arches/lit-legion/tasks/${taskId}`,
    );
  }

  async updateLitLegionTaskStatus(taskId: string, status: string, userInput?: string) {
    return this.request<{ taskStatus: LitLegionTaskStatusResponse }>(
      'POST',
      `/arches/lit-legion/tasks/${taskId}/status`,
      { status, ...(userInput !== undefined ? { userInput } : {}) },
    );
  }

  async getLitLegionStats() {
    return this.request<{ stats: LitLegionPlayerStats }>('GET', '/arches/lit-legion/stats');
  }

  // Lit Legion Admin Methods

  async adminListLitLegionTasks(options?: {
    category?: string;
    taskType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.taskType) params.set('taskType', options.taskType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LitLegionAdminTaskListResult>(
      'GET',
      `/arches/lit-legion/admin/tasks${qs ? `?${qs}` : ''}`,
    );
  }

  async createLitLegionTask(input: CreateLitLegionTaskPayload) {
    return this.request<{ task: LitLegionAdminTask }>(
      'POST',
      '/arches/lit-legion/admin/tasks',
      input,
    );
  }

  async adminDeleteLitLegionTask(taskId: string) {
    return this.request<void>('DELETE', `/arches/lit-legion/admin/tasks/${taskId}`);
  }

  async adminListLitLegionNpcs() {
    return this.request<{ npcs: LitLegionNpc[] }>('GET', '/arches/lit-legion/admin/npcs');
  }

  async adminListLitLegionLevels() {
    return this.request<{ levels: LitLegionUserLevel[] }>('GET', '/arches/lit-legion/admin/levels');
  }

  async adminListLitLegionBadges() {
    return this.request<{ badges: LitLegionBadge[] }>('GET', '/arches/lit-legion/admin/badges');
  }

  // ==========================================================================
  // Files API
  // ==========================================================================

  /**
   * Get presigned URL for single file upload
   */
  async getUploadUrl(params: {
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    category?: FileCategory;
    checksumSha256?: string;
  }) {
    return this.request<PresignedUploadResponse>('POST', '/files/upload-url', params);
  }

  /**
   * Initiate multipart upload for large files
   */
  async initiateMultipartUpload(params: {
    fileName: string;
    mimeType: string;
    sizeBytes: number;
    category?: FileCategory;
  }) {
    return this.request<MultipartUploadResponse>('POST', '/files/upload-url/multipart', params);
  }

  /**
   * Get presigned URL for a specific part of multipart upload
   */
  async getMultipartPartUrl(params: {
    uploadId: string;
    fileId: string;
    s3Key: string;
    partNumber: number;
  }) {
    return this.request<PresignedPartResponse>('POST', '/files/upload-url/multipart/part', params);
  }

  /**
   * Complete multipart upload
   */
  async completeMultipartUpload(params: {
    uploadId: string;
    fileId: string;
    s3Key: string;
    parts: Array<{ partNumber: number; etag: string }>;
  }) {
    return this.request<{ location: string }>(
      'POST',
      '/files/upload-url/multipart/complete',
      params,
    );
  }

  /**
   * Confirm file upload and create metadata record
   */
  async confirmUpload(
    fileId: string,
    params: {
      fileName: string;
      mimeType: string;
      sizeBytes: number;
      s3Key: string;
      category?: FileCategory;
      checksumSha256?: string;
      folderId?: string;
      tags?: string[];
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ file: UserFileResponse }>('POST', `/files/${fileId}/confirm`, params);
  }

  /**
   * List user files
   */
  async listFiles(options?: {
    folderId?: string | null;
    category?: FileCategory;
    status?: FileStatus;
    tags?: string[];
    search?: string;
    sortBy?: 'name' | 'created' | 'updated' | 'accessed' | 'size';
    sortOrder?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.folderId !== undefined) params.set('folderId', String(options.folderId));
    if (options?.category) params.set('category', options.category);
    if (options?.status) params.set('status', options.status);
    if (options?.tags?.length) params.set('tags', options.tags.join(','));
    if (options?.search) params.set('search', options.search);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<FileListResult>('GET', `/files${query ? `?${query}` : ''}`);
  }

  /**
   * Get file metadata
   */
  async getFile(fileId: string) {
    return this.request<{ file: UserFileResponse }>('GET', `/files/${fileId}`);
  }

  /**
   * Get presigned download URL for a file
   */
  async getDownloadUrl(fileId: string) {
    return this.request<{ downloadUrl: string; expiresAt: string }>(
      'GET',
      `/files/${fileId}/download-url`,
    );
  }

  /**
   * Update file metadata
   */
  async updateFile(
    fileId: string,
    updates: {
      fileName?: string;
      folderId?: string | null;
      category?: FileCategory;
      tags?: string[];
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ file: UserFileResponse }>('PATCH', `/files/${fileId}`, updates);
  }

  /**
   * Delete a file (soft delete)
   */
  async deleteFile(fileId: string) {
    return this.request<void>('DELETE', `/files/${fileId}`);
  }

  /**
   * Permanently delete a file (removes from DB and S3, cannot be undone)
   */
  async permanentDeleteFile(fileId: string) {
    return this.request<void>('DELETE', `/files/${fileId}/permanent`);
  }

  /**
   * Restore a file from Trash to its original folder
   */
  async restoreFromTrash(fileId: string) {
    return this.request<{
      file: { fileId: string; fileName: string; folderId: string | null };
      message: string;
    }>('POST', `/files/${fileId}/restore-from-trash`);
  }

  /**
   * Archive a file to Glacier storage
   */
  async archiveFile(fileId: string) {
    return this.request<{ file: UserFileResponse }>('POST', `/files/${fileId}/archive`);
  }

  /**
   * Restore a file from archive
   */
  async restoreFile(fileId: string) {
    return this.request<{ file: UserFileResponse; message: string }>(
      'POST',
      `/files/${fileId}/restore`,
    );
  }

  /**
   * Bulk file operations
   */
  async bulkFileOperation(params: {
    operation: 'delete' | 'move' | 'tag' | 'restore-from-trash' | 'permanent-delete';
    fileIds: string[];
    folderId?: string | null;
    tags?: string[];
  }) {
    return this.request<{
      operation: string;
      requestedCount: number;
      affectedCount: number;
    }>('POST', '/files/bulk', params);
  }

  // ==========================================================================
  // Folders API
  // ==========================================================================

  /**
   * Create a new folder
   */
  async createFolder(params: { name: string; parentFolderId?: string }) {
    return this.request<{ folder: FolderResponse }>('POST', '/folders', params);
  }

  /**
   * List folders
   */
  async listFolders(parentFolderId?: string | null) {
    const params = new URLSearchParams();
    if (parentFolderId !== undefined) params.set('parentFolderId', String(parentFolderId));
    const query = params.toString();
    return this.request<{ folders: FolderResponse[] }>(
      'GET',
      `/folders${query ? `?${query}` : ''}`,
    );
  }

  /**
   * Get folder details
   */
  async getFolder(folderId: string) {
    return this.request<{ folder: FolderResponse }>('GET', `/folders/${folderId}`);
  }

  /**
   * Get folder breadcrumb path
   */
  async getFolderBreadcrumb(folderId: string) {
    return this.request<{ breadcrumb: FolderResponse[] }>('GET', `/folders/${folderId}/breadcrumb`);
  }

  /**
   * Update folder (rename or move)
   */
  async updateFolder(
    folderId: string,
    updates: {
      name?: string;
      parentFolderId?: string | null;
    },
  ) {
    return this.request<{ folder: FolderResponse }>('PATCH', `/folders/${folderId}`, updates);
  }

  /**
   * Delete a folder
   */
  async deleteFolder(folderId: string, deleteContents: boolean = false) {
    const params = new URLSearchParams();
    if (deleteContents) params.set('deleteContents', 'true');
    const query = params.toString();
    return this.request<void>('DELETE', `/folders/${folderId}${query ? `?${query}` : ''}`);
  }

  /**
   * Ensure My-Fias/Context folder exists
   * Creates the protected folder structure if it doesn't exist.
   * Returns the Context folder info.
   */
  async ensureContextFolder() {
    return this.request<{ folder: ContextFolderResponse }>('POST', '/folders/context');
  }

  /**
   * Ensure the My-Fias/AI Images folder exists (creates if missing)
   */
  async ensureAIImagesFolder() {
    return this.request<{ folder: FolderResponse }>('POST', '/folders/ai-images');
  }

  /**
   * Ensure the My-Fias/Reference Images folder exists (creates if missing)
   */
  async ensureReferenceImagesFolder() {
    return this.request<{ folder: FolderResponse }>('POST', '/folders/reference-images');
  }

  /**
   * Ensure the My-Fias/Trash folder exists (creates if missing)
   */
  async ensureTrashFolder() {
    return this.request<{ folder: FolderResponse }>('POST', '/folders/trash');
  }

  // ==========================================================================
  // Storage API
  // ==========================================================================

  /**
   * Get storage usage and quota
   */
  async getStorageUsage() {
    return this.request<StorageUsageResponse>('GET', '/storage');
  }

  /**
   * List file operations for audit
   */
  async listFileOperations(options?: {
    fileId?: string;
    operationType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.fileId) params.set('fileId', options.fileId);
    if (options?.operationType) params.set('operationType', options.operationType);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<FileOperationsListResult>(
      'GET',
      `/storage/operations${query ? `?${query}` : ''}`,
    );
  }

  // ==========================================================================
  // Image Generation API
  // ==========================================================================

  async generateImage(archeId: string, input: ImageGenerationInput) {
    return this.request<ImageGenerationResponse>('POST', `/arches/${archeId}/generate-image`, {
      input,
    });
  }

  async cancelImageGeneration(archeId: string, requestId: string) {
    return this.request<{ status: string; requestId: string }>(
      'POST',
      `/arches/${archeId}/cancel-generation/${requestId}`,
    );
  }

  async listImageModels(archeId: string) {
    return this.request<ImageModelsResponse>('GET', `/arches/${archeId}/image-models`);
  }

  async getImageUrl(archeId: string, imageRef: string): Promise<string> {
    const result = await this.request<{ url: string }>(
      'GET',
      `/arches/${archeId}/image-url?imageRef=${encodeURIComponent(imageRef)}`,
    );
    return result.url;
  }

  async checkImageStatus(
    archeId: string,
    requestId: string,
  ): Promise<{
    status: 'pending' | 'complete' | 'error';
    imageRef?: string;
    url?: string;
    error?: string;
  }> {
    return this.request<{
      status: 'pending' | 'complete' | 'error';
      imageRef?: string;
      url?: string;
      error?: string;
    }>('GET', `/arches/${archeId}/image-status/${requestId}`);
  }

  // ==========================================================================
  // Icon Generator API
  // ==========================================================================

  async generateIcon(input: {
    name: string;
    description: string;
    useDefaultStyle?: boolean;
    style?: string;
  }) {
    return this.request<{ requestId: string }>('POST', '/arches/icon-generator/generate', input);
  }

  async getIconGeneratorStatus(requestId: string) {
    return this.request<{
      status: 'pending' | 'complete' | 'error';
      previewUrl?: string;
      requestId?: string;
      error?: string;
    }>('GET', `/arches/icon-generator/status/${requestId}`);
  }

  async saveIcon(input: { requestId: string; fileName?: string }) {
    return this.request<{ fileId: string; imageRef: string }>(
      'POST',
      '/arches/icon-generator/save',
      input,
    );
  }

  // ==========================================================================
  // Image Editor API
  // ==========================================================================

  async processImage(request: {
    sourceFileId?: string;
    sourceDataUrl?: string;
    operations: Array<Record<string, unknown>>;
    output: {
      format: 'png' | 'jpeg' | 'webp';
      quality?: number;
      width?: number;
      height?: number;
      maintainAspectRatio?: boolean;
    };
    saveAs?: { fileName: string };
  }) {
    return this.request<{
      fileId?: string;
      downloadUrl?: string;
      width: number;
      height: number;
      sizeBytes: number;
      format: string;
    }>('POST', '/arches/image-editor/process', request);
  }

  // ==========================================================================
  // Builder Sessions API
  // ==========================================================================

  async createBuilderSession(params?: { templateId?: string; existingArchId?: string }) {
    return this.request<{
      sessionId: string;
      status: 'active';
      template?: BuilderTemplate;
      existingArche?: unknown;
    }>('POST', '/arches/builder/sessions', params || {});
  }

  async listBuilderSessions() {
    return this.request<{ sessions: BuilderSessionSummary[] }>('GET', '/arches/builder/sessions');
  }

  async getBuilderSession(sessionId: string) {
    return this.request<{ session: BuilderSession; currentPreview: unknown }>(
      'GET',
      `/arches/builder/sessions/${sessionId}`,
    );
  }

  async updateBuilderSession(
    sessionId: string,
    updates: {
      currentName?: string;
      currentDescription?: string;
      currentSettings?: unknown;
      status?: 'active' | 'paused' | 'completed' | 'abandoned';
    },
  ) {
    return this.request<{ session: BuilderSession }>(
      'PATCH',
      `/arches/builder/sessions/${sessionId}`,
      updates,
    );
  }

  async deleteBuilderSession(sessionId: string) {
    return this.request<void>('DELETE', `/arches/builder/sessions/${sessionId}`);
  }

  async resetBuilderContext(sessionId: string) {
    return this.request<{ session: BuilderSession; currentPreview: unknown }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/reset-context`,
    );
  }

  async checkArcheName(name: string, excludeArchId?: string) {
    const params = new URLSearchParams({ name });
    if (excludeArchId) {
      params.append('excludeArchId', excludeArchId);
    }
    return this.request<{ available: boolean }>(
      'GET',
      `/arches/builder/check-name?${params.toString()}`,
    );
  }

  async sendBuilderMessage(sessionId: string, message: string) {
    return this.request<{
      response: ClaudeMessageResponse;
      updatedPreview: unknown;
    }>('POST', `/arches/builder/sessions/${sessionId}/message`, { message });
  }

  async startBuilderMessageStream(
    sessionId: string,
    message: string,
    verbose?: boolean,
    useOpus?: boolean,
    guidedPhase?: string,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
      status: 'pending';
    }>('POST', `/arches/builder/sessions/${sessionId}/message/stream`, {
      message,
      verbose,
      useOpus,
      ...(guidedPhase ? { guidedPhase } : {}),
    });
  }

  async startGuidedFlow(sessionId: string, idea: string) {
    return this.request<{
      guidedFlow: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: unknown[];
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
    }>('POST', `/arches/builder/sessions/${sessionId}/guided/start`, { idea });
  }

  async saveBuilderSession(sessionId: string, params: { name: string; description: string }) {
    return this.request<{ archId: string; status: 'draft'; version: number }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/save`,
      params,
    );
  }

  async uploadPreviewScreenshot(sessionId: string, dataUrl: string): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/preview-screenshot`,
      { screenshot: dataUrl },
    );
  }

  async submitProblemReport(
    sessionId: string,
    report: {
      description: string;
      problemScope: number;
      conversationHistory?: unknown;
      currentUiDefinition?: unknown;
      currentSettings?: unknown;
      logEntries?: unknown;
      telemetryState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      `/arches/builder/sessions/${sessionId}/report-problem`,
      report,
    );
  }

  // ==========================================================================
  // Plugin Builder API
  // ==========================================================================

  async createPluginBuilderSession(params?: { name?: string }) {
    return this.request<{
      sessionId: string;
      status: 'active';
      currentFiles: Record<string, unknown>;
    }>('POST', '/arches/plugin-builder/sessions', params || {});
  }

  async listPluginBuilderSessions() {
    return this.request<{ sessions: unknown[] }>('GET', '/arches/plugin-builder/sessions');
  }

  async getPluginBuilderSession(sessionId: string) {
    return this.request<{ session: unknown }>(
      'GET',
      `/arches/plugin-builder/sessions/${sessionId}`,
    );
  }

  async updatePluginBuilderSession(
    sessionId: string,
    updates: {
      currentName?: string;
      currentDescription?: string;
      currentExpandedDescription?: string;
      currentSettings?: unknown;
      status?: 'active' | 'paused' | 'completed' | 'abandoned';
    },
  ) {
    return this.request<unknown>('PATCH', `/arches/plugin-builder/sessions/${sessionId}`, updates);
  }

  async updatePluginBuilderSessionFiles(sessionId: string, currentFiles: Record<string, unknown>) {
    return this.request<unknown>('PATCH', `/arches/plugin-builder/sessions/${sessionId}`, {
      currentSettings: { currentFiles },
    });
  }

  async deletePluginBuilderSession(sessionId: string) {
    return this.request<void>('DELETE', `/arches/plugin-builder/sessions/${sessionId}`);
  }

  async startPluginBuilderMessageStream(
    sessionId: string,
    message: string,
    verbose?: boolean,
    useOpus?: boolean,
    guidedPhase?: string,
    autoApprove?: boolean,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
      status: 'pending';
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/message/stream`, {
      message,
      verbose,
      useOpus,
      ...(guidedPhase ? { guidedPhase } : {}),
      ...(autoApprove !== undefined ? { autoApprove } : {}),
    });
  }

  async advancePluginBuilderStep(sessionId: string) {
    return this.request<{
      sessionId: string;
      buildPlan: unknown;
      allVerified: boolean;
      nextStep: { id: string; title: string } | null;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/guided/advance-step`);
  }

  async startPluginGuidedFlow(
    sessionId: string,
    idea: string,
    verbose?: boolean,
    signal?: AbortSignal,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/start`,
      { idea, verbose },
      { signal },
    );
  }

  async submitGuidedAnswers(
    sessionId: string,
    answers: Record<number, string>,
    verbose?: boolean,
    signal?: AbortSignal,
    comment?: string,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/answers`,
      { answers, verbose, ...(comment ? { comment } : {}) },
      { signal },
    );
  }

  async submitGuidedPlanDecision(
    sessionId: string,
    action: 'approve' | 'request-changes',
    verbose?: boolean,
    feedback?: string,
    signal?: AbortSignal,
  ) {
    return this.request<{
      requestId: string;
      sessionId: string;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/plan-decision`,
      {
        action,
        verbose,
        ...(feedback ? { feedback } : {}),
      },
      { signal },
    );
  }

  async submitGuidedAssumptionDecision(
    sessionId: string,
    action: 'approve' | 'suggest-changes',
    verbose?: boolean,
    comments?: Record<string, string>,
    signal?: AbortSignal,
  ) {
    // approve returns full result synchronously; suggest-changes returns { requestId } for streaming
    return this.request<{
      sessionId: string;
      requestId?: string;
      guidedFlow?: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: Array<{ question: string; answer: string | null }>;
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
      buildPlan?: unknown;
    }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/guided/assumption-decision`,
      {
        action,
        verbose,
        ...(comments ? { comments } : {}),
      },
      { signal },
    );
  }

  async rewindGuidedPhase(sessionId: string, targetPhase: string) {
    return this.request<{
      sessionId: string;
      guidedFlow: {
        phase: string;
        ideaSummary: string;
        clarifyingQA: Array<{ question: string; answer: string | null }>;
        assumptions: unknown[];
        requireStepApproval: boolean;
        lastPhaseTransition: string;
      };
      buildPlan: unknown;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/guided/rewind`, {
      targetPhase,
    });
  }

  async buildPluginPreview(sessionId: string, files?: Record<string, unknown>) {
    return this.request<{
      success: boolean;
      html: string;
      fileCount: number;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/build`, files ? { files } : {});
  }

  async savePluginBuilderSession(
    sessionId: string,
    params: { name: string; description?: string },
  ) {
    return this.request<{ success: boolean; archId: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/save`,
      params,
    );
  }

  async uploadPluginPreviewScreenshot(sessionId: string, dataUrl: string): Promise<void> {
    await this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/preview-screenshot`,
      { dataUrl },
    );
  }

  async resetPluginBuilderContext(sessionId: string) {
    return this.request<{ success: boolean }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/reset-context`,
    );
  }

  async getPluginPublishCost(sessionId: string) {
    return this.request<{
      isFirstPublish: boolean;
      totalCents: number;
      reviewFeeCents: number;
      listingFeeCents: number;
    }>('GET', `/arches/plugin-builder/sessions/${sessionId}/publish-cost`);
  }

  async publishPluginBuilder(sessionId: string) {
    return this.request<{
      submissionId: string;
      archId: string;
      reviewId: string;
      status: string;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/publish`);
  }

  async unpublishPluginSession(sessionId: string) {
    return this.request<{
      archId: string;
      status: string;
      isListed: boolean;
    }>('POST', `/arches/plugin-builder/sessions/${sessionId}/unpublish`);
  }

  async submitPluginProblemReport(
    sessionId: string,
    report: {
      description: string;
      problemScope: number;
      conversationHistory?: unknown;
      currentFiles?: unknown;
      currentSettings?: unknown;
      guidedFlow?: unknown;
      buildPlan?: unknown;
      logEntries?: unknown;
      telemetryState?: unknown;
      guidedBuilderState?: unknown;
    },
  ) {
    return this.request<{ reportId: string; status: string; createdAt: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/report-problem`,
      report,
    );
  }

  async invokePluginAI(
    sessionId: string,
    input: string,
    entityId: string,
  ): Promise<{ output: string }> {
    return this.request<{ output: string }>(
      'POST',
      `/arches/plugin-builder/sessions/${sessionId}/ai-invoke`,
      { input, entityId },
    );
  }

  // ==========================================================================
  // Builder Templates API
  // ==========================================================================

  async listBuilderTemplates(options?: { category?: string; featured?: boolean }) {
    const params = new URLSearchParams();
    if (options?.category) params.set('category', options.category);
    if (options?.featured !== undefined) params.set('featured', String(options.featured));
    const query = params.toString();
    return this.request<{ templates: BuilderTemplate[] }>(
      'GET',
      `/arches/builder/templates${query ? `?${query}` : ''}`,
    );
  }

  async getBuilderTemplate(templateId: string) {
    return this.request<{ template: BuilderTemplate }>(
      'GET',
      `/arches/builder/templates/${templateId}`,
    );
  }

  // ==========================================================================
  // Builder Reviews API
  // ==========================================================================

  async requestArcheReview(archId: string) {
    return this.request<{
      reviewId: string;
      status: 'pending';
      estimatedCostCents: number;
    }>('POST', `/contributor/arches/${archId}/request-review`);
  }

  async listArcheReviews(archId: string) {
    return this.request<{ reviews: ArcheReviewSummary[] }>(
      'GET',
      `/contributor/arches/${archId}/reviews`,
    );
  }

  async getArcheReview(archId: string, reviewId: string) {
    return this.request<{ review: ArcheReviewDetail }>(
      'GET',
      `/contributor/arches/${archId}/reviews/${reviewId}`,
    );
  }

  // ==========================================================================
  // Chat API
  // ==========================================================================

  // Channel operations

  async listChatChannels(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<ChatChannelListResult>(
      'GET',
      `/arches/chat/channels${query ? `?${query}` : ''}`,
    );
  }

  async createChatChannel(input: CreateChatChannelInput) {
    return this.request<{ channel: ChatChannelResponse }>('POST', '/arches/chat/channels', input);
  }

  async getChatChannel(channelId: string) {
    return this.request<{ channel: ChatChannelResponse }>(
      'GET',
      `/arches/chat/channels/${channelId}`,
    );
  }

  async updateChatChannel(channelId: string, input: { name?: string; description?: string }) {
    return this.request<{ channel: ChatChannelResponse }>(
      'PATCH',
      `/arches/chat/channels/${channelId}`,
      input,
    );
  }

  async deleteChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}`);
  }

  async getGlobalChatChannel() {
    return this.request<{ channel: ChatChannelResponse }>('GET', '/arches/chat/channels/global');
  }

  async getOrCreateDirectChannel(target: string) {
    const isEmail = target.includes('@');
    return this.request<{ channel: ChatChannelResponse }>('POST', '/arches/chat/channels/direct', {
      ...(isEmail ? { targetEmail: target } : { targetUsername: target }),
    });
  }

  async joinChatChannel(channelId: string) {
    return this.request<{ member: ChatChannelMemberResponse }>(
      'POST',
      `/arches/chat/channels/${channelId}/join`,
    );
  }

  async leaveChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/leave`);
  }

  async getChatChannelMembers(channelId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<{ members: ChatChannelMemberResponse[]; total: number }>(
      'GET',
      `/arches/chat/channels/${channelId}/members${query ? `?${query}` : ''}`,
    );
  }

  async updateChatMemberRole(channelId: string, userId: string, role: string) {
    return this.request<{ member: ChatChannelMemberResponse }>(
      'PATCH',
      `/arches/chat/channels/${channelId}/members/${userId}`,
      { role },
    );
  }

  async removeChatMember(channelId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/members/${userId}`);
  }

  async muteChatChannel(channelId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/mute`);
  }

  async unmuteChatChannel(channelId: string) {
    return this.request<void>('DELETE', `/arches/chat/channels/${channelId}/mute`);
  }

  async markChatChannelRead(channelId: string, messageId: string) {
    return this.request<void>('POST', `/arches/chat/channels/${channelId}/read`, { messageId });
  }

  async getChatUnreadCounts() {
    return this.request<{ channels: ChatUnreadCount[] }>('GET', '/arches/chat/unread');
  }

  // Message operations

  async listChatMessages(
    channelId: string,
    options?: { before?: string; after?: string; limit?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.before) params.set('before', options.before);
    if (options?.after) params.set('after', options.after);
    if (options?.limit) params.set('limit', options.limit.toString());
    const query = params.toString();
    return this.request<ChatMessageListResult>(
      'GET',
      `/arches/chat/channels/${channelId}/messages${query ? `?${query}` : ''}`,
    );
  }

  async sendChatMessage(
    channelId: string,
    input: {
      content: string;
      replyToId?: string;
      mentions?: Array<{ userId: string; displayName: string }>;
    },
  ) {
    return this.request<{ message: ChatMessageResponse }>(
      'POST',
      `/arches/chat/channels/${channelId}/messages`,
      input,
    );
  }

  async editChatMessage(messageId: string, content: string) {
    return this.request<{ message: ChatMessageResponse }>(
      'PATCH',
      `/arches/chat/messages/${messageId}`,
      {
        content,
      },
    );
  }

  async deleteChatMessage(messageId: string) {
    return this.request<void>('DELETE', `/arches/chat/messages/${messageId}`);
  }

  async searchChatMessages(
    channelId: string,
    query: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    params.set('q', query);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    return this.request<{ messages: ChatMessageResponse[]; total: number }>(
      'GET',
      `/arches/chat/channels/${channelId}/messages/search?${params.toString()}`,
    );
  }

  async getChatPinnedMessages(channelId: string) {
    return this.request<{ pins: ChatPinnedMessageResponse[] }>(
      'GET',
      `/arches/chat/channels/${channelId}/pins`,
    );
  }

  async pinChatMessage(messageId: string) {
    return this.request<void>('POST', `/arches/chat/messages/${messageId}/pin`);
  }

  async unpinChatMessage(messageId: string) {
    return this.request<void>('DELETE', `/arches/chat/messages/${messageId}/pin`);
  }

  async addChatReaction(messageId: string, emoji: string) {
    return this.request<void>('POST', `/arches/chat/messages/${messageId}/reactions`, { emoji });
  }

  async removeChatReaction(messageId: string, emoji: string) {
    return this.request<void>(
      'DELETE',
      `/arches/chat/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
    );
  }

  // Moderation operations

  async reportChatMessage(input: {
    messageId?: string;
    channelId?: string;
    reason: string;
    description?: string;
  }) {
    return this.request<{ report: { reportId: string } }>('POST', '/arches/chat/reports', input);
  }

  async blockChatUser(blockedId: string) {
    return this.request<void>('POST', '/arches/chat/blocks', { blockedId });
  }

  async unblockChatUser(blockedId: string) {
    return this.request<void>('DELETE', `/arches/chat/blocks/${blockedId}`);
  }

  async listChatBlockedUsers() {
    return this.request<{ blocks: ChatBlockResponse[] }>('GET', '/arches/chat/blocks');
  }

  // ==========================================================================
  // Guild Methods
  // ==========================================================================

  async listGuilds(options?: {
    search?: string;
    guildType?: string;
    sortBy?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.guildType) params.set('guildType', options.guildType);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildListResult>('GET', `/arches/guilds${qs ? `?${qs}` : ''}`);
  }

  async getGuildDetail(guildId: string) {
    return this.request<GuildDetailResponse>('GET', `/arches/guilds/${guildId}/detail`);
  }

  async getGuildInviteInfo(inviteCode: string) {
    return this.request<{ guild: GuildSummary; invite: GuildInviteInfo }>(
      'GET',
      `/arches/guilds/invite/${inviteCode}`,
    );
  }

  async createGuild(input: CreateGuildPayload) {
    return this.request<GuildDetailResponse>('POST', '/arches/guilds', input);
  }

  async updateGuild(guildId: string, input: UpdateGuildPayload) {
    return this.request<GuildDetailResponse>('PATCH', `/arches/guilds/${guildId}`, input);
  }

  async deleteGuild(guildId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}`);
  }

  async getMyGuilds() {
    return this.request<{ guilds: GuildSummary[] }>('GET', '/arches/guilds/me');
  }

  async joinGuild(guildId: string) {
    return this.request<GuildMemberResponse>('POST', `/arches/guilds/${guildId}/join`);
  }

  async leaveGuild(guildId: string) {
    return this.request<void>('POST', `/arches/guilds/${guildId}/leave`);
  }

  async getGuildMembers(
    guildId: string,
    options?: { role?: string; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.role) params.set('role', options.role);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildMemberListResult>(
      'GET',
      `/arches/guilds/${guildId}/members${qs ? `?${qs}` : ''}`,
    );
  }

  async updateGuildMemberRole(guildId: string, userId: string, role: string) {
    return this.request<GuildMemberResponse>(
      'PATCH',
      `/arches/guilds/${guildId}/members/${userId}`,
      {
        role,
      },
    );
  }

  async removeGuildMember(guildId: string, userId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/members/${userId}`);
  }

  async transferGuildOwnership(guildId: string, newOwnerId: string) {
    return this.request<{ success: boolean }>('POST', `/arches/guilds/${guildId}/transfer`, {
      newOwnerId,
    });
  }

  async createGuildInvite(
    guildId: string,
    options?: { maxUses?: number; expiresInHours?: number },
  ) {
    return this.request<GuildInviteResponse>(
      'POST',
      `/arches/guilds/${guildId}/invites`,
      options || {},
    );
  }

  async listGuildInvites(guildId: string) {
    return this.request<{ invites: GuildInviteResponse[] }>(
      'GET',
      `/arches/guilds/${guildId}/invites`,
    );
  }

  async revokeGuildInvite(guildId: string, inviteId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/invites/${inviteId}`);
  }

  async acceptGuildInvite(inviteCode: string) {
    return this.request<GuildMemberResponse>('POST', `/arches/guilds/invite/${inviteCode}/accept`);
  }

  async banGuildMember(guildId: string, userId: string, reason?: string) {
    return this.request<GuildBanResponse>('POST', `/arches/guilds/${guildId}/bans`, {
      userId,
      reason,
    });
  }

  async unbanGuildMember(guildId: string, banId: string) {
    return this.request<void>('DELETE', `/arches/guilds/${guildId}/bans/${banId}`);
  }

  async listGuildBans(guildId: string) {
    return this.request<{ bans: GuildBanResponse[] }>('GET', `/arches/guilds/${guildId}/bans`);
  }

  async listGuildActivity(
    guildId: string,
    options?: { activityType?: string; limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.activityType) params.set('activityType', options.activityType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GuildActivityResult>(
      'GET',
      `/arches/guilds/${guildId}/activity${qs ? `?${qs}` : ''}`,
    );
  }

  async getGuildTreasury(guildId: string) {
    return this.request<GuildTreasuryResponse>('GET', `/arches/guilds/${guildId}/treasury`);
  }

  async listGuildTreasuryTransactions(
    guildId: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{ transactions: GuildTreasuryTransactionResponse[] }>(
      'GET',
      `/arches/guilds/${guildId}/treasury/transactions${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Learn Anything - Course API
  // ==========================================================================

  async listCourses(options?: {
    status?: string;
    difficultyLevel?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.difficultyLevel) params.set('difficultyLevel', options.difficultyLevel);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      courses: Array<{
        courseId: string;
        slug: string;
        title: string;
        description: string | null;
        difficultyLevel: string;
        estimatedDurationHours: number | null;
        coverImageRef: string | null;
        categoryId: string | null;
        tags: string[];
        status: string;
        createdAt: string;
        updatedAt: string;
        publishedAt: string | null;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/learn-anything/courses${qs ? `?${qs}` : ''}`);
  }

  async getCourseCurriculum(slug: string) {
    return this.request<{
      courseId: string;
      slug: string;
      title: string;
      description: string | null;
      difficultyLevel: string;
      estimatedDurationHours: number | null;
      tags: string[];
      status: string;
      modules: Array<{
        moduleId: string;
        title: string;
        description: string | null;
        sequenceOrder: number;
        estimatedDurationMinutes: number | null;
        lessons: Array<{
          lessonId: string;
          title: string;
          lessonType: string;
          sequenceOrder: number;
          estimatedDurationMinutes: number | null;
        }>;
      }>;
    }>('GET', `/arches/learn-anything/courses/${slug}/curriculum`);
  }

  async enrollCourse(slug: string) {
    return this.request<{ enrollmentId: string; status: string }>(
      'POST',
      `/arches/learn-anything/courses/${slug}/enroll`,
    );
  }

  async getCourseProgress(slug: string) {
    return this.request<{
      enrollmentId: string | null;
      lessons: Record<string, { correctCount: number; targetQuizCount: number; status: string }>;
    }>('GET', `/arches/learn-anything/courses/${slug}/progress`);
  }

  async updateLessonProgress(
    lessonId: string,
    data: { correctCount: number; targetQuizCount: number; status: string },
  ) {
    return this.request<{ success: boolean }>(
      'PATCH',
      `/arches/learn-anything/lessons/${lessonId}/progress`,
      data,
    );
  }

  // ==========================================================================
  // DFE Inventory API
  // ==========================================================================

  async listDfeDefinitions(options?: {
    search?: string;
    fungibility?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.fungibility) params.set('fungibility', options.fungibility);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeDefinitionListResult>(
      'GET',
      `/arches/dfe/definitions${qs ? `?${qs}` : ''}`,
    );
  }

  async getDfeDefinition(itemDefId: string) {
    return this.request<DfeDefinitionDetail>('GET', `/arches/dfe/definitions/${itemDefId}`);
  }

  async getDfeInventory(options?: {
    search?: string;
    fungibility?: string;
    category?: string;
    sortBy?: string;
    sortOrder?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.fungibility) params.set('fungibility', options.fungibility);
    if (options?.category !== undefined) params.set('category', options.category);
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeInventoryListResult>(
      'GET',
      `/arches/dfe/inventory${qs ? `?${qs}` : ''}`,
    );
  }

  async getDfeInventoryItem(itemId: string) {
    return this.request<DfeInventoryItemDetail>('GET', `/arches/dfe/inventory/${itemId}`);
  }

  async transferDfeItem(input: TransferDfeItemInput) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/transfer', input);
  }

  async burnDfeItem(itemId: string) {
    return this.request<{ success: boolean }>('DELETE', `/arches/dfe/inventory/${itemId}`);
  }

  async burnDfeFungibleItems(input: {
    itemDefId: string;
    quantity: number;
    category?: string;
    position?: string;
  }) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/inventory/burn-fungible', input);
  }

  async createDfeDefinition(input: CreateDfeDefinitionInput) {
    return this.request<DfeDefinitionDetail>('POST', '/arches/dfe/admin/definitions', input);
  }

  async updateDfeDefinition(
    itemDefId: string,
    input: UpdateDfeDefinitionInput | Record<string, unknown>,
  ) {
    return this.request<DfeDefinitionDetail>(
      'PATCH',
      `/arches/dfe/admin/definitions/${itemDefId}`,
      input,
    );
  }

  async archiveDfeDefinition(itemDefId: string) {
    return this.request<{ itemDefId: string; name: string; status: string }>(
      'DELETE',
      `/arches/dfe/admin/definitions/${itemDefId}`,
    );
  }

  async mintDfeItems(input: {
    itemDefId: string;
    toUsername: string;
    quantity: number;
    metadata?: Record<string, unknown>;
    notes?: string;
    category?: string;
    position?: string;
  }) {
    return this.request<DfeMintResult>('POST', '/arches/dfe/admin/mint', input);
  }

  async listDfeTransfers(options?: {
    itemDefId?: string;
    userId?: string;
    transferType?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.itemDefId) params.set('itemDefId', options.itemDefId);
    if (options?.userId) params.set('userId', options.userId);
    if (options?.transferType) params.set('transferType', options.transferType);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<DfeTransferListResult>(
      'GET',
      `/arches/dfe/admin/transfers${qs ? `?${qs}` : ''}`,
    );
  }

  async updateDfeItemPlacement(itemId: string, input: { category?: string; position?: string }) {
    return this.request<{ itemId: string; category: string; position: string }>(
      'PATCH',
      `/arches/dfe/inventory/${itemId}/placement`,
      input,
    );
  }

  async moveDfeFungibleStack(input: {
    itemDefId: string;
    quantity: number;
    fromCategory: string;
    fromPosition: string;
    toCategory: string;
    toPosition: string;
  }) {
    return this.request<{ success: boolean }>('POST', '/arches/dfe/inventory/move-fungible', input);
  }

  async getDfePositionConflicts() {
    return this.request<{ conflicts: DfePositionConflict[] }>(
      'GET',
      '/arches/dfe/inventory/position-conflicts',
    );
  }

  // =========================================================================
  // DFE Inventory Transition
  // =========================================================================

  async importDfeLegacyInventory(input: { username: string; password: string }) {
    return this.request<DfeLegacyImportResult>(
      'POST',
      '/arches/dfe-inventory-transition/import',
      input,
    );
  }

  async getDfeLegacyImportStatus() {
    return this.request<{ imports: DfeLegacyImportEntry[] }>(
      'GET',
      '/arches/dfe-inventory-transition/import/status',
    );
  }

  async retryDfeLegacyImport(importId: string, password: string) {
    return this.request<DfeLegacyImportResult>(
      'POST',
      `/arches/dfe-inventory-transition/import/${importId}/retry`,
      { password },
    );
  }

  async checkDfeLegacyUsername(username: string) {
    const params = new URLSearchParams({ username });
    return this.request<{ imported: boolean }>(
      'GET',
      `/arches/dfe-inventory-transition/import/check?${params.toString()}`,
    );
  }

  async getGuildChatChannel(guildId: string) {
    return this.request<{ channelId: string }>('GET', `/arches/guilds/${guildId}/channel`);
  }

  // ==========================================================================
  // Game Rewards Methods
  // ==========================================================================

  // Public
  async getGameRewardsTiers() {
    return this.request<GrTierListResult>('GET', '/arches/game-rewards/tiers');
  }

  // User - Subscription
  async getGameRewardsSubscription() {
    return this.request<{ subscription: GrSubscriptionResponse | null }>(
      'GET',
      '/arches/game-rewards/subscription',
    );
  }

  async purchaseGameRewardsSubscription(tier: string) {
    return this.request<{ subscription: GrSubscriptionResponse }>(
      'POST',
      '/arches/game-rewards/subscription/purchase',
      { tier },
    );
  }

  async cancelGameRewardsSubscription() {
    return this.request<{ success: boolean }>('POST', '/arches/game-rewards/subscription/cancel');
  }

  async getGameRewardsSubscriptionHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrSubscriptionHistoryResult>(
      'GET',
      `/arches/game-rewards/subscription/history${qs ? `?${qs}` : ''}`,
    );
  }

  // User - Rewards
  async getGameRewardsHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardLogListResult>(
      'GET',
      `/arches/game-rewards/rewards${qs ? `?${qs}` : ''}`,
    );
  }

  // Admin - Games
  async listGameRewardsGames(options?: { isActive?: boolean; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrGameConfigListResult>(
      'GET',
      `/arches/game-rewards/admin/games${qs ? `?${qs}` : ''}`,
    );
  }

  async getGameRewardsGame(gameConfigId: string) {
    return this.request<GrGameConfigDetail>(
      'GET',
      `/arches/game-rewards/admin/games/${gameConfigId}`,
    );
  }

  async createGameRewardsGame(input: {
    gameName: string;
    displayName: string;
    description?: string;
    minScore?: number;
    maxScore?: number;
  }) {
    return this.request<GrGameConfigCreateResult>(
      'POST',
      '/arches/game-rewards/admin/games',
      input,
    );
  }

  async updateGameRewardsGame(
    gameConfigId: string,
    input: {
      displayName?: string;
      description?: string;
      minScore?: number;
      maxScore?: number;
      rewardMode?: GrRewardMode;
      allowedItems?: string[] | null;
      isActive?: boolean;
    },
  ) {
    return this.request<GrGameConfigDetail>(
      'PATCH',
      `/arches/game-rewards/admin/games/${gameConfigId}`,
      input,
    );
  }

  async rotateGameRewardsApiKey(gameConfigId: string) {
    return this.request<{ gameConfigId: string; apiKey: string; apiKeyPrefix: string }>(
      'POST',
      `/arches/game-rewards/admin/games/${gameConfigId}/rotate-key`,
    );
  }

  async deleteGameRewardsGame(gameConfigId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/games/${gameConfigId}`,
    );
  }

  // Admin - Reward Rules
  async listGameRewardsRules(options?: {
    gameConfigId?: string;
    gameEvent?: string;
    isActive?: boolean;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.gameEvent) params.set('gameEvent', options.gameEvent);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardRuleListResult>(
      'GET',
      `/arches/game-rewards/admin/rules${qs ? `?${qs}` : ''}`,
    );
  }

  async createGameRewardsRule(input: {
    gameConfigId: string;
    gameEvent: string;
    rewardType: string;
    itemDefId: string;
    m: number;
    b: number;
    maxRewardCount?: number;
    bronzeMultiplier?: number;
    silverMultiplier?: number;
    goldMultiplier?: number;
    steps?: GrStepDefinition[] | null;
    baseMultiplier?: number;
  }) {
    return this.request<GrRewardRuleResponse>('POST', '/arches/game-rewards/admin/rules', input);
  }

  async updateGameRewardsRule(
    ruleId: string,
    input: {
      m?: number;
      b?: number;
      maxRewardCount?: number;
      bronzeMultiplier?: number;
      silverMultiplier?: number;
      goldMultiplier?: number;
      steps?: GrStepDefinition[] | null;
      baseMultiplier?: number;
      isActive?: boolean;
      itemDefId?: string;
    },
  ) {
    return this.request<GrRewardRuleResponse>(
      'PATCH',
      `/arches/game-rewards/admin/rules/${ruleId}`,
      input,
    );
  }

  async deleteGameRewardsRule(ruleId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/rules/${ruleId}`,
    );
  }

  // Admin - Rate Limits
  async listGameRewardsLimits(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardTypeLimitListResult>(
      'GET',
      `/arches/game-rewards/admin/limits${qs ? `?${qs}` : ''}`,
    );
  }

  async createGameRewardsLimit(input: {
    rewardType: string;
    minSecondsBetweenRewards?: number;
    maxRewardsPerDay?: number;
  }) {
    return this.request<GrRewardTypeLimitResponse>(
      'POST',
      '/arches/game-rewards/admin/limits',
      input,
    );
  }

  async updateGameRewardsLimit(
    limitId: string,
    input: { minSecondsBetweenRewards?: number; maxRewardsPerDay?: number },
  ) {
    return this.request<GrRewardTypeLimitResponse>(
      'PATCH',
      `/arches/game-rewards/admin/limits/${limitId}`,
      input,
    );
  }

  async deleteGameRewardsLimit(limitId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/limits/${limitId}`,
    );
  }

  // Admin - Tier Config
  async listGameRewardsAdminTiers() {
    return this.request<GrAdminTierListResult>('GET', '/arches/game-rewards/admin/tiers');
  }

  async createGameRewardsTier(input: {
    tier: string;
    displayName: string;
    currencyItemDefId: string;
    priceAmount: number;
    durationDays: number;
    description?: string;
  }) {
    return this.request<GrTierConfigResponse>('POST', '/arches/game-rewards/admin/tiers', input);
  }

  async updateGameRewardsTier(
    tierId: string,
    input: {
      displayName?: string;
      currencyItemDefId?: string;
      priceAmount?: number;
      durationDays?: number;
      description?: string;
      isActive?: boolean;
    },
  ) {
    return this.request<GrTierConfigResponse>(
      'PATCH',
      `/arches/game-rewards/admin/tiers/${tierId}`,
      input,
    );
  }

  // Admin - Subscriptions
  async listGameRewardsSubscriptions(options?: {
    userId?: string;
    isActive?: boolean;
    tier?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.userId) params.set('userId', options.userId);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.tier) params.set('tier', options.tier);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrAdminSubscriptionListResult>(
      'GET',
      `/arches/game-rewards/admin/subscriptions${qs ? `?${qs}` : ''}`,
    );
  }

  async adminSetGameRewardsSubscription(input: {
    username: string;
    tier: string;
    durationDays: number;
  }) {
    return this.request<GrSubscriptionResponse>(
      'POST',
      '/arches/game-rewards/admin/subscriptions',
      input,
    );
  }

  async adminRemoveGameRewardsSubscription(subscriptionId: string) {
    return this.request<{ success: boolean }>(
      'DELETE',
      `/arches/game-rewards/admin/subscriptions/${subscriptionId}`,
    );
  }

  // Admin - Reward Log
  async listGameRewardsLog(options?: {
    userId?: string;
    gameConfigId?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.userId) params.set('userId', options.userId);
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrRewardLogListResult>(
      'GET',
      `/arches/game-rewards/admin/reward-log${qs ? `?${qs}` : ''}`,
    );
  }

  // User - Time Played
  async getGameRewardsTimePlayed() {
    return this.request<GrTimePlayedAggregates>('GET', '/arches/game-rewards/time-played');
  }

  // User - Leaderboard
  async getGameRewardsLeaderboard(
    gameConfigId: string,
    gameEvent: string,
    options?: { limit?: number; offset?: number },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrLeaderboardResult>(
      'GET',
      `/arches/game-rewards/leaderboard/${gameConfigId}/${gameEvent}${qs ? `?${qs}` : ''}`,
    );
  }

  // User - My Scores
  async getGameRewardsMyScores(options?: {
    gameConfigId?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.gameConfigId) params.set('gameConfigId', options.gameConfigId);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GrUserBestScoresResult>(
      'GET',
      `/arches/game-rewards/my-scores${qs ? `?${qs}` : ''}`,
    );
  }

  // Public - Leaderboard Games
  async getGameRewardsLeaderboardGames() {
    return this.request<{ games: GrLeaderboardGameInfo[] }>(
      'GET',
      '/arches/game-rewards/leaderboard/games',
    );
  }

  // ==========================================================================
  // DFE Game State API
  // ==========================================================================

  async getGameState(keyword: string) {
    return this.request<{ gameState: GameStateResponse }>(
      'GET',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}`,
    );
  }

  async setGameState(
    keyword: string,
    body: {
      dataType: string;
      data?: string;
      dataBinary?: string;
      version?: number | null;
      checksum?: string;
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ gameState: GameStateResponse }>(
      'PUT',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}`,
      body,
    );
  }

  async deleteGameState(keyword: string, version: number) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/state/${encodeURIComponent(keyword)}?version=${version}`,
    );
  }

  async listGameStates(options?: { limit?: number; offset?: number; keyword?: string }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    if (options?.keyword) params.set('keyword', options.keyword);
    const qs = params.toString();
    return this.request<GameStateListResult>(
      'GET',
      `/arches/dfe-game-state/states${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Admin User API
  // ==========================================================================

  async adminLookupUser(username: string) {
    return this.request<AdminUserLookupResult>(
      'GET',
      `/admin/users/lookup?username=${encodeURIComponent(username)}`,
    );
  }

  // ==========================================================================
  // Admin Arche Icons API
  // ==========================================================================

  async adminGetArcheIconEnvironments() {
    return this.request<{ environments: string[] }>('GET', '/admin/arches/icon/environments');
  }

  async adminGetArcheIconUploadUrls(
    archId: string,
    mimeType: string,
    sizeBytes: number,
    environments: string[],
  ) {
    return this.request<{
      uploads: Record<string, { uploadUrl: string; s3Key: string; cdnUrl: string }>;
      expiresAt: string;
    }>('POST', `/admin/arches/${encodeURIComponent(archId)}/icon/upload-urls`, {
      mimeType,
      sizeBytes,
      environments,
    });
  }

  async adminConfirmArcheIcon(archId: string, s3Key: string, environments: string[]) {
    return this.request<{
      arche: { archId: string; name: string; imageRef: string };
      results: Record<string, { verified: boolean; cdnUrl: string }>;
    }>('PUT', `/admin/arches/${encodeURIComponent(archId)}/icon/confirm`, { s3Key, environments });
  }

  async adminDeleteArcheIcon(archId: string, environments?: string[]) {
    return this.request<void>('DELETE', `/admin/arches/${encodeURIComponent(archId)}/icon`, {
      environments,
    });
  }

  // ==========================================================================
  // Admin Invite Codes API
  // ==========================================================================

  async adminGetInviteCodeEnvironments() {
    return this.request<{ environments: string[] }>('GET', '/admin/invite-codes/environments');
  }

  async adminCreateInviteCodes(options: {
    count?: number;
    expiresAt?: string;
    grantRoles?: string[];
    metadata?: { description?: string; notes?: string };
    environment?: string;
  }) {
    return this.request<{
      inviteCode?: InviteCodeResponse;
      inviteCodes?: InviteCodeResponse[];
      count?: number;
    }>('POST', '/admin/invite-codes', options);
  }

  async adminListInviteCodes(options?: { status?: string; limit?: number; environment?: string }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.environment) params.set('environment', options.environment);
    const qs = params.toString();
    return this.request<{ inviteCodes: InviteCodeResponse[]; count: number }>(
      'GET',
      `/admin/invite-codes${qs ? `?${qs}` : ''}`,
    );
  }

  async adminRevokeInviteCode(code: string, environment?: string) {
    const params = new URLSearchParams();
    if (environment) params.set('environment', environment);
    const qs = params.toString();
    return this.request<{ message: string; code: string }>(
      'DELETE',
      `/admin/invite-codes/${encodeURIComponent(code)}${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // DFE Game State Admin API
  // ==========================================================================

  async adminListGameStates(
    userId: string,
    options?: { limit?: number; offset?: number; keyword?: string },
  ) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    if (options?.keyword) params.set('keyword', options.keyword);
    const qs = params.toString();
    return this.request<GameStateListResult>(
      'GET',
      `/arches/dfe-game-state/admin/users/${encodeURIComponent(userId)}/states${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetGameState(userId: string, keyword: string) {
    return this.request<{ gameState: GameStateResponse }>(
      'GET',
      `/arches/dfe-game-state/admin/users/${encodeURIComponent(userId)}/state/${encodeURIComponent(keyword)}`,
    );
  }

  async adminSetGameState(
    userId: string,
    keyword: string,
    body: {
      dataType: string;
      data?: string;
      dataBinary?: string;
      checksum?: string;
      metadata?: Record<string, unknown>;
    },
  ) {
    return this.request<{ gameState: GameStateResponse }>(
      'PUT',
      `/arches/dfe-game-state/admin/users/${encodeURIComponent(userId)}/state/${encodeURIComponent(keyword)}`,
      body,
    );
  }

  async adminDeleteGameState(userId: string, keyword: string) {
    return this.request<void>(
      'DELETE',
      `/arches/dfe-game-state/admin/users/${encodeURIComponent(userId)}/state/${encodeURIComponent(keyword)}`,
    );
  }

  async adminListGameStateAuditLog(options?: {
    userId?: string;
    keyword?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.userId) params.set('userId', options.userId);
    if (options?.keyword) params.set('keyword', options.keyword);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<GameStateAuditLogResult>(
      'GET',
      `/arches/dfe-game-state/admin/audit${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Polls API
  // ==========================================================================

  async createPoll(input: CreatePollPayload) {
    return this.request<PollDetailResponse>('POST', '/arches/polls', input);
  }

  async listMyPolls(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const qs = params.toString();
    return this.request<PollListResult>('GET', `/arches/polls${qs ? `?${qs}` : ''}`);
  }

  async getPoll(pollId: string) {
    return this.request<PollFullDetailResponse>('GET', `/arches/polls/${pollId}`);
  }

  async updatePoll(pollId: string, input: UpdatePollPayload) {
    return this.request<PollDetailResponse>('PUT', `/arches/polls/${pollId}`, input);
  }

  async deletePoll(pollId: string) {
    return this.request<void>('DELETE', `/arches/polls/${pollId}`);
  }

  async publishPoll(pollId: string) {
    return this.request<{ pollId: string; status: string; updatedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/publish`,
    );
  }

  async closePoll(pollId: string) {
    return this.request<{ pollId: string; status: string; updatedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/close`,
    );
  }

  async submitPollResponse(pollId: string, input: { answers: PollAnswerPayload[] }) {
    return this.request<{ responseId: string; pollId: string; submittedAt: string }>(
      'POST',
      `/arches/polls/${pollId}/respond`,
      input,
    );
  }

  async getMyPollResponse(pollId: string) {
    return this.request<PollResponseResult>('GET', `/arches/polls/${pollId}/my-response`);
  }

  async getPollResults(pollId: string) {
    return this.request<PollResultsResponse>('GET', `/arches/polls/${pollId}/results`);
  }

  async viewPoll(pollId: string) {
    return this.request<PollPublicViewResponse>('GET', `/arches/polls/${pollId}/view`);
  }

  // ==========================================================================
  // Plugin Submission API
  // ==========================================================================

  async getPluginUploadUrl(body: { fileName: string; sizeBytes: number; checksumSha256?: string }) {
    return this.request<{
      uploadUrl: string;
      submissionId: string;
      s3Key: string;
      expiresAt: string;
    }>('POST', '/plugins/submissions/upload-url', body);
  }

  async createPluginSubmission(body: {
    submissionId: string;
    sourceRef: string;
    manifest: Record<string, unknown>;
    archId?: string;
  }) {
    return this.request<{
      submissionId: string;
      archId: string;
      reviewId: string;
      status: string;
      createdAt: string;
    }>('POST', '/plugins/submissions', body);
  }

  async listPluginSubmissions(options?: { status?: string; limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    const query = params.toString();
    return this.request<PluginSubmissionListResult>(
      'GET',
      `/plugins/submissions${query ? `?${query}` : ''}`,
    );
  }

  async getPluginSubmission(submissionId: string) {
    return this.request<PluginSubmissionDetailResult>(
      'GET',
      `/plugins/submissions/${submissionId}`,
    );
  }

  async deletePluginSubmission(submissionId: string) {
    return this.request<{ success: boolean; submissionId: string }>(
      'DELETE',
      `/plugins/submissions/${submissionId}`,
    );
  }

  // ========================================================================
  // Plugin Admin API
  // ========================================================================

  async listEscalatedPluginReviews(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      reviews: Array<{
        reviewId: string;
        submissionId: string;
        archId: string;
        manifestName: string;
        riskScore: number | null;
        criticalCount: number;
        highCount: number;
        filesAnalyzed: number;
        createdAt: string;
      }>;
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/admin/plugins/reviews/escalated${qs ? `?${qs}` : ''}`);
  }

  async getPluginReviewDetail(reviewId: string) {
    return this.request<{
      review: {
        reviewId: string;
        submissionId: string;
        reviewerType: string;
        status: string;
        riskScore: number | null;
        codeFindings: unknown[] | null;
        dependencyFindings: unknown[] | null;
        policyFindings: unknown[] | null;
        qualityFindings: unknown[] | null;
        criticalCount: number;
        highCount: number;
        filesAnalyzed: number;
        dependenciesAnalyzed: number;
        reviewedBy: string | null;
        humanNotes: string | null;
        createdAt: string;
        completedAt: string | null;
      };
      submission: {
        submissionId: string;
        archId: string;
        manifestName: string;
        manifestVersion: string;
        status: string;
        sourceRef: string;
        createdAt: string;
      } | null;
    }>('GET', `/admin/plugins/reviews/${reviewId}`);
  }

  async approvePluginReview(reviewId: string, notes?: string) {
    return this.request<{ success: boolean; reviewId: string; status: string }>(
      'POST',
      `/admin/plugins/reviews/${reviewId}/approve`,
      { notes },
    );
  }

  async rejectPluginReview(reviewId: string, notes: string) {
    return this.request<{ success: boolean; reviewId: string; status: string }>(
      'POST',
      `/admin/plugins/reviews/${reviewId}/reject`,
      { notes },
    );
  }

  // ==========================================================================
  // OAuth Consent API
  // ==========================================================================

  async getOAuthAuthorizeInfo(params: Record<string, string>) {
    const qs = new URLSearchParams(params).toString();
    return this.request<OAuthAuthorizeInfo>('GET', `/oauth/authorize?${qs}`);
  }

  async submitOAuthConsent(params: {
    clientId: string;
    redirectUri: string;
    responseType: string;
    scope: string;
    state: string;
    codeChallenge: string;
    codeChallengeMethod: string;
    consent: 'granted' | 'denied';
  }) {
    return this.request<{ redirectUrl: string }>('POST', '/oauth/authorize', params);
  }

  // ==========================================================================
  // Connected Apps API
  // ==========================================================================

  async getConnectedApps() {
    return this.request<{ apps: ConnectedApp[] }>('GET', '/settings/connected-apps');
  }

  async revokeConnectedApp(appId: string) {
    return this.request<void>('DELETE', `/settings/connected-apps/${appId}`);
  }

  // ==========================================================================
  // Crafting API
  // ==========================================================================

  async createCraftingRecipe(input: Record<string, unknown>) {
    return this.request<{ recipe: { recipeId: string } }>(
      'POST',
      '/arches/crafting/admin/recipes',
      input,
    );
  }

  async updateCraftingRecipe(recipeId: string, input: Record<string, unknown>) {
    return this.request<{ recipe: { recipeId: string } }>(
      'PATCH',
      `/arches/crafting/admin/recipes/${recipeId}`,
      input,
    );
  }

  // ==========================================================================
  // Mining API
  // ==========================================================================

  async getMiningLands() {
    return this.request<{
      lands: Array<{
        landId: string;
        userId: string;
        landType: string;
        xSize: number;
        ySize: number;
        zSize: number;
        generation: number;
        paidReset: boolean;
        createdAt: string;
        updatedAt: string;
      }>;
    }>('GET', '/arches/mining/lands');
  }

  async getMiningLand(landType: string) {
    return this.request<{
      landType: string;
      dimensions: { x: number; y: number; z: number };
      blocks: Record<string, string>;
      generation: number;
      paidReset: boolean;
    }>('GET', `/arches/mining/lands/${landType}`);
  }

  async getMiningLog(options?: { limit?: number; offset?: number }) {
    const params: Record<string, string> = {};
    if (options?.limit) params.limit = String(options.limit);
    if (options?.offset) params.offset = String(options.offset);

    const queryString = Object.keys(params).length
      ? '?' + new URLSearchParams(params).toString()
      : '';

    return this.request<{
      entries: Array<{
        logId: string;
        userId: string;
        landId: string;
        landType: string;
        blocksMined: number;
        itemsMinted: Array<{ itemName: string; quantity: number }>;
        createdAt: string;
      }>;
      total: number;
    }>('GET', `/arches/mining/log${queryString}`);
  }

  // ==========================================================================
  // Liquidity Caches
  // ==========================================================================

  async listLiquidityCachePools(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      pools: LcPoolResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/liquidity-caches${qs ? `?${qs}` : ''}`);
  }

  async getLiquidityCachePool(poolId: string) {
    return this.request<LcPoolResponse>('GET', `/arches/liquidity-caches/${poolId}`);
  }

  async getLiquidityCacheQuote(
    poolId: string,
    direction: string,
    inputAmount: string,
    niceMode?: boolean,
  ) {
    const params = new URLSearchParams();
    params.set('direction', direction);
    params.set('inputAmount', inputAmount);
    if (niceMode) params.set('niceMode', 'true');
    return this.request<{
      amountOut: string;
      feeAmount: string;
      priceImpactBps: number;
      niceMode?: { adjustedInput: string; cleanOutput: string; roundingSavings: string };
    }>('GET', `/arches/liquidity-caches/${poolId}/quote?${params.toString()}`);
  }

  async liquidityCacheInvest(
    poolId: string,
    input: { tokenAAmount: string; tokenBAmount: string },
  ) {
    return this.request<{
      position: { positionId: string; poolId: string; lpShares: string };
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/invest`, input);
  }

  async liquidityCacheWithdraw(poolId: string, input: { lpShares: string }) {
    return this.request<{
      amountA: string;
      amountB: string;
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/withdraw`, input);
  }

  async liquidityCacheTrade(
    poolId: string,
    input: {
      direction: string;
      inputAmount: string;
      minOutputAmount?: string;
      deadline?: string;
      niceMode?: boolean;
    },
  ) {
    return this.request<{
      amountOut: string;
      transaction: LcTransactionResponse;
    }>('POST', `/arches/liquidity-caches/${poolId}/trade`, input);
  }

  async getMyLiquidityCachePositions() {
    return this.request<{
      positions: Array<{
        positionId: string;
        poolId: string;
        lpShares: string;
        createdAt: string;
        updatedAt: string;
        pool?: {
          poolId: string;
          name: string;
          tokenASymbol: string;
          tokenBSymbol: string;
          reserveA: string;
          reserveB: string;
          totalLpShares: string;
          feeBps: number;
          status: string;
        };
      }>;
      total: number;
    }>('GET', '/arches/liquidity-caches/me/positions');
  }

  async getMyLiquidityCacheTransactions(options?: {
    type?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.type) params.set('type', options.type);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<{
      transactions: LcTransactionResponse[];
      total: number;
      limit: number;
      offset: number;
    }>('GET', `/arches/liquidity-caches/me/transactions${qs ? `?${qs}` : ''}`);
  }

  async getLiquidityCachePosition(poolId: string) {
    return this.request<{
      position: {
        positionId: string;
        poolId: string;
        lpShares: string;
        createdAt: string;
        updatedAt: string;
      } | null;
    }>('GET', `/arches/liquidity-caches/${poolId}/position`);
  }

  async createLiquidityCachePool(input: {
    name: string;
    description?: string;
    tokenA: { itemDefId?: string; symbol: string; name: string; decimals: number; source: string };
    tokenB: { itemDefId?: string; symbol: string; name: string; decimals: number; source: string };
    feeBps?: number;
    minLiquidity?: number;
    maxTradePct?: number;
  }) {
    return this.request<LcPoolResponse>('POST', '/arches/liquidity-caches/admin', input);
  }

  async updateLiquidityCachePool(
    poolId: string,
    input: {
      name?: string;
      description?: string;
      feeBps?: number;
      maxTradePct?: number;
      status?: string;
    },
  ) {
    return this.request<LcPoolResponse>('PATCH', `/arches/liquidity-caches/admin/${poolId}`, input);
  }

  // ==========================================================================
  // Fiardle Methods
  // ==========================================================================

  async createFiardleGame() {
    return this.request<{ gameId: string }>('POST', '/arches/fiardle/games');
  }

  async getFiardleActiveGame() {
    return this.request<{ game: FiardleGameResponse }>('GET', '/arches/fiardle/games/active');
  }

  async getFiardleGame(gameId: string) {
    return this.request<{ game: FiardleGameResponse }>('GET', `/arches/fiardle/games/${gameId}`);
  }

  async submitFiardleGuess(gameId: string, word: string) {
    return this.request<FiardleGuessResponse>('POST', `/arches/fiardle/games/${gameId}/guesses`, {
      word,
    });
  }

  async getFiardleGameHistory(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<FiardleGameHistoryResponse>(
      'GET',
      `/arches/fiardle/games${qs ? `?${qs}` : ''}`,
    );
  }

  async getFiardleStats(userId?: string) {
    const path = userId ? `/arches/fiardle/stats/${userId}` : '/arches/fiardle/stats';
    return this.request<FiardleStatsResponse>('GET', path);
  }

  async getFiardleLeaderboard(options?: {
    sortBy?: 'wins' | 'winRate' | 'streak';
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<FiardleLeaderboardResponse>(
      'GET',
      `/arches/fiardle/leaderboard${qs ? `?${qs}` : ''}`,
    );
  }

  // ==========================================================================
  // Business Methods
  // ==========================================================================

  async listBusinesses() {
    return this.request<{ businesses: BusinessResponse[] }>('GET', '/arches/businesses');
  }

  async getBusiness(businessId: string) {
    return this.request<BusinessResponse>('GET', `/arches/businesses/${businessId}`);
  }

  async createBusiness(input: { businessType: string; name: string }) {
    return this.request<BusinessResponse>('POST', '/arches/businesses', input);
  }

  async dissolveBusiness(businessId: string) {
    return this.request<void>('DELETE', `/arches/businesses/${businessId}`);
  }

  async collectBusinessRevenue(
    businessId: string,
    input: { goods: Record<string, number>; foods: Record<string, number> },
  ) {
    return this.request<CollectRevenueResponse>(
      'POST',
      `/arches/businesses/${businessId}/collect`,
      input,
    );
  }

  async getBusinessParameters(type?: string) {
    const params = new URLSearchParams();
    if (type) params.set('type', type);
    const qs = params.toString();
    return this.request<{ parameters: BusinessParameterResponse[] }>(
      'GET',
      `/arches/businesses/parameters${qs ? `?${qs}` : ''}`,
    );
  }

  async getBusinessHistory(businessId: string, options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<BusinessHistoryResponse>(
      'GET',
      `/arches/businesses/${businessId}/history${qs ? `?${qs}` : ''}`,
    );
  }

  async getBusinessStats() {
    return this.request<BusinessStatsResponse>('GET', '/arches/businesses/admin/stats');
  }

  // ==========================================================================
  // API Keys
  // ==========================================================================

  async createApiKey(params: { name: string; scopes?: string[]; expiresAt?: string }) {
    return this.request<{
      keyId: string;
      name: string;
      keyPrefix: string;
      secret: string;
      scopes: string[];
      createdAt: string;
    }>('POST', '/api-keys', params);
  }

  async listApiKeys() {
    return this.request<{
      keys: Array<{
        keyId: string;
        name: string;
        keyPrefix: string;
        scopes: string[];
        status: string;
        createdAt: string;
        lastUsedAt: string | null;
        expiresAt: string | null;
      }>;
    }>('GET', '/api-keys');
  }

  // ===========================================================================
  // Beta Access
  // ===========================================================================

  async submitBetaAccessRequest(input: BetaAccessSubmitInput) {
    return this.request<BetaAccessSubmitResponse>('POST', '/arches/beta-access/requests', input);
  }

  async listBetaAccessRequests(options?: {
    status?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }) {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.search) params.set('search', options.search);
    if (options?.limit != null) params.set('limit', String(options.limit));
    if (options?.offset != null) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<BetaAccessListResponse>(
      'GET',
      `/arches/beta-access/admin/requests${qs ? `?${qs}` : ''}`,
    );
  }

  async getBetaAccessRequest(requestId: string) {
    return this.request<BetaAccessRequestResponse>(
      'GET',
      `/arches/beta-access/admin/requests/${requestId}`,
    );
  }

  async updateBetaAccessRequestStatus(
    requestId: string,
    input: { status: 'accepted' | 'rejected'; adminNotes?: string },
  ) {
    return this.request<BetaAccessRequestResponse>(
      'PATCH',
      `/arches/beta-access/admin/requests/${requestId}`,
      input,
    );
  }

  async getBetaAccessStats() {
    return this.request<BetaAccessStatsResponse>('GET', '/arches/beta-access/admin/stats');
  }

  // ==========================================================================
  // Learn About Fias
  // ==========================================================================

  async getLearnAboutFiasResources() {
    return this.request<LearnAboutFiasResourcesResponse>(
      'GET',
      '/arches/learn-about-fias/resources',
    );
  }

  async adminListLearnAboutFiasDocuments(options?: { limit?: number; offset?: number }) {
    const params = new URLSearchParams();
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    const qs = params.toString();
    return this.request<LearnAboutFiasDocumentListResponse>(
      'GET',
      `/arches/learn-about-fias/admin/documents${qs ? `?${qs}` : ''}`,
    );
  }

  async adminGetLearnAboutFiasDocument(docId: string) {
    return this.request<LearnAboutFiasDocumentDetailResponse>(
      'GET',
      `/arches/learn-about-fias/admin/documents/${docId}`,
    );
  }

  async adminCreateLearnAboutFiasDocument(
    input:
      | {
          mode: 'file';
          title: string;
          description?: string;
          fileBase64: string;
          fileName: string;
          sourceUrl?: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        }
      | {
          mode: 'text';
          title: string;
          description?: string;
          content: string;
          sourceType: string;
          sourceUrl?: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        }
      | {
          mode: 'link';
          title: string;
          description?: string;
          sourceUrl: string;
          showInResources?: boolean;
          ragEnabled?: boolean;
        },
  ) {
    return this.request<{ docId: string; chunksCreated: number; status: string }>(
      'POST',
      '/arches/learn-about-fias/admin/documents',
      input,
    );
  }

  async adminUpdateLearnAboutFiasDocument(
    docId: string,
    input: {
      title?: string;
      description?: string | null;
      sourceUrl?: string | null;
      showInResources?: boolean;
      ragEnabled?: boolean;
    },
  ) {
    return this.request<LearnAboutFiasDocumentResponse>(
      'PATCH',
      `/arches/learn-about-fias/admin/documents/${docId}`,
      input,
    );
  }

  async adminUpdateLearnAboutFiasDocumentContent(
    docId: string,
    input:
      | { mode: 'file'; fileBase64: string; fileName: string }
      | { mode: 'text'; content: string; sourceType?: string },
  ) {
    return this.request<{ docId: string; chunksCreated: number; status: string }>(
      'PATCH',
      `/arches/learn-about-fias/admin/documents/${docId}/content`,
      input,
    );
  }

  async adminDeleteLearnAboutFiasDocument(docId: string) {
    return this.request<void>('DELETE', `/arches/learn-about-fias/admin/documents/${docId}`);
  }

  async adminGetLearnAboutFiasStats() {
    return this.request<LearnAboutFiasStatsResponse>('GET', '/arches/learn-about-fias/admin/stats');
  }
}

// ==========================================================================
// Liquidity Cache Types
// ==========================================================================

export interface LcPoolResponse {
  poolId: string;
  name: string;
  description: string | null;
  tokenA: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  tokenB: {
    itemDefId: string | null;
    symbol: string;
    name: string;
    decimals: number;
    source: string;
  };
  reserveA: string;
  reserveB: string;
  totalLpShares: string;
  feeBps: number;
  minLiquidity: number;
  maxTradePct: number;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface LcTransactionResponse {
  transactionId: string;
  poolId: string;
  transactionType: string;
  tokenAAmount: string | null;
  tokenBAmount: string | null;
  lpSharesAmount: string | null;
  tradeDirection: string | null;
  feeAmount: string | null;
  priceAtTrade: string | null;
  slippageBps: number | null;
  roundingAdjusted: boolean;
  originalAmount: string | null;
  adjustedAmount: string | null;
  roundingSavings: string | null;
  inventoryTransfersExecuted: boolean;
  createdAt: string;
}

// ==========================================================================
// Conversation Types
// ==========================================================================

export interface ConversationResponse {
  conversationId: string;
  archeId: string;
  title: string | null;
  messageCount: number;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
}

export interface ConversationListResult {
  conversations: ConversationResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface ConversationMessageInput {
  role: 'user' | 'assistant';
  content: string;
  thinking?: string;
  provider?: ModelProvider;
  modelName?: string;
  modelEntityId?: string;
  inputTokens?: number;
  outputTokens?: number;
  thinkingTokens?: number;
  costUsd?: number;
  latencyMs?: number;
  metadata?: Record<string, unknown>;
}

export interface ConversationMessageResponse {
  messageId: string;
  sequenceNum: number;
  role: 'user' | 'assistant';
  content: string;
  thinking: string | null;
  provider: ModelProvider | null;
  modelName: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  costUsd: number | null;
  latencyMs: number | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ConversationWithMessages extends ConversationResponse {
  messages: ConversationMessageResponse[];
}

export interface DeleteAllConversationsResult {
  success: boolean;
  deletedCount: number;
}

export interface ShareConversationResponse {
  shareId: string;
  expiresAt: string;
}

export interface SharedMessage {
  role: 'user' | 'assistant';
  content: string;
  provider: ModelProvider | null;
  modelName: string | null;
  timestamp: string;
}

export interface SharedConversationResponse {
  shareId: string;
  archeId: string;
  title: string | null;
  messages: SharedMessage[];
  createdAt: string;
  expiresAt: string;
}

// ==========================================================================
// Arche Session Types
// ==========================================================================

export interface SingleAIPreferences {
  activeConversationId: string | null;
  selectedProvider: ModelProvider;
  selectedModelEntityId: string | null;
  includeContext: boolean;
  showThinking: boolean;
  providerSettings: {
    openai?: {
      gpt4MaxOutputTokens?: number;
      gpt5Reasoning?: boolean;
      reasoningEffort?: 'low' | 'medium' | 'high' | 'xhigh';
      verbosity?: 'low' | 'medium' | 'high';
      gptMaxOutputTokens?: number;
    };
    anthropic?: {
      maxTokens?: number;
      budgetTokens?: number;
      temperature?: number;
    };
    google?: {
      maxOutputTokens?: number;
      temperature?: number;
      thinkingLevel?: 'LOW' | 'HIGH';
    };
  };
}

export interface MultiAIColumnConfig {
  provider: ModelProvider;
  modelEntityId: string;
  showThinking: boolean;
  gpt5Reasoning?: boolean;
  providerSettings: Record<string, unknown>;
}

export interface MultiAIPreferences {
  activeConversationId: string | null;
  columnConfigs: MultiAIColumnConfig[];
  selectedTier: 'fast' | 'balanced' | 'powerful' | null;
  includeContext: boolean;
  enabledPanels: boolean[];
}

export interface SingleAISessionResponse {
  type: 'single-ai';
  archeId: string;
  preferences: SingleAIPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface MultiAISessionResponse {
  type: 'multi-ai';
  archeId: string;
  preferences: MultiAIPreferences;
  createdAt: string;
  updatedAt: string;
}

export interface ImageGeneratorPreferences {
  selectedProvider: 'openai' | 'google' | 'bfl' | 'recraft';
  selectedModelId: string | null;
  imageSettings: {
    size: string;
    quality: string;
    style: string;
  };
}

export interface ImageGeneratorSessionResponse {
  type: 'image-generator';
  archeId: string;
  preferences: ImageGeneratorPreferences;
  createdAt: string;
  updatedAt: string;
}

export type ArcheSessionResponse =
  | SingleAISessionResponse
  | MultiAISessionResponse
  | ImageGeneratorSessionResponse;

export type ArcheSessionUpdateInput =
  | Partial<SingleAIPreferences>
  | Partial<MultiAIPreferences>
  | Partial<ImageGeneratorPreferences>;

// ==========================================================================
// Arche Metadata Types
// ==========================================================================

/**
 * Lightweight arche metadata for route protection and embed eligibility
 */
export interface ArcheMetadata {
  archId: string;
  isPublic: boolean;
  isFullScreen: boolean;
  hasWorkflow: boolean;
  /** Whether the arche can be embedded in an iframe (public and no workflow) */
  isEmbeddable: boolean;
  renderMode: string;
  sessionType: string;
  isTrusted: boolean;
  suggestedArches: string[];
  redirectPath: string | null;
}

// ==========================================================================
// Favorites Types
// ==========================================================================

export interface UserFavoriteWithArche {
  userId: string;
  archId: string;
  position: number;
  createdAt: string;
  archeName: string;
  archeSimpleDescription: string;
  archeImageRef: string | null;
}

// ==========================================================================
// Marketplace Types
// ==========================================================================

export type ArcheType = 'tool' | 'site';

export interface MarketplaceArche {
  archId: string;
  slug: string | null;
  name: string;
  simpleDescription: string;
  imageRef: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  tags: string[];
  fitnessScore: number;
  invocationCount: number;
  pricing: ArchePricing;
  actionPricing: ActionPricing | null;
  hasPerUseCredits: boolean;
  isStatic: boolean;
  archeType: ArcheType;
  relevance?: number;
  contributorId: string;
  isPublic: boolean;
  isFullScreen: boolean;
}

export interface MarketplaceSearchResult {
  arches: MarketplaceArche[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export interface MarketplaceArcheDetail {
  archId: string;
  slug: string | null;
  name: string;
  simpleDescription: string;
  expandedDescription: string | null;
  imageRef: string | null;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  tags: string[];
  fitnessScore: number;
  successRate: number;
  avgCompletionTimeMs: number | null;
  invocationCount: number;
  pricing: ArchePricing;
  actionPricing: ActionPricing | null;
  hasPerUseCredits: boolean;
  costPerformanceConfig: CostPerformanceConfig;
  isStatic: boolean;
  archeType: ArcheType;
  inputSchema: Record<string, unknown> | null;
  contributorId: string;
  publishedAt: string | null;
  isPublic: boolean;
  isFullScreen: boolean;
}

export interface MarketplaceCategory {
  categoryId: string;
  name: string;
  slug: string;
  description: string;
  parentCategoryId: string | null;
  depth: number;
  archeCount: number;
}

export interface ArchePricing {
  model: 'free' | 'fixed' | 'per_use' | 'tiered' | 'no_base_cost' | 'included';
  currency: 'usd';
  priceCents?: number;
  baseFeeCents?: number;
  platformFeePercent?: number;
  tiers?: Array<{ upTo: number; priceCents: number }>;
}

export interface ActionPrice {
  costCredits: number;
  platformFeePercent: number;
}

export type ActionPricing = Record<string, ActionPrice>;

export interface CostPerformanceConfig {
  defaultWeight: number;
  userAdjustable: boolean;
  minWeight: number | null;
  maxWeight: number | null;
}

// ==========================================================================
// Marketplace Entity Types
// ==========================================================================

export type EntityType =
  | 'specialized-model'
  | 'human'
  | 'integration'
  | 'code-block'
  | 'model-definition'
  | 'service';

export interface EntityPricing {
  model: 'free' | 'fixed' | 'per_invocation' | 'no_base_cost' | 'included';
  currency: 'usd';
  priceCents?: number;
}

export interface MarketplaceEntity {
  entityId: string;
  type: EntityType;
  name: string;
  description: string;
  categoryId: string | null;
  categoryName: string | null;
  categorySlug: string | null;
  fitnessScore: number;
  invocationCount: number;
  avgLatencyMs: number | null;
  successRate: number;
  pricing: EntityPricing;
  contributorId: string;
  relevance?: number;
  semanticSimilarity?: number;
}

export interface MarketplaceEntityDetail extends MarketplaceEntity {
  inputSchema: Record<string, unknown>;
  outputSchema: Record<string, unknown>;
  publishedAt: string | null;
  modelProvider?: string;
  modelName?: string;
}

export interface MarketplaceEntitySearchResult {
  entities: MarketplaceEntity[];
  total: number;
  limit: number;
  offset: number;
  hasMore: boolean;
  query?: string;
}

export interface MarketplaceCategoryWithEntityCount {
  categoryId: string;
  name: string;
  slug: string;
  description: string;
  parentCategoryId: string | null;
  depth: number;
  entityCount: number;
}

// ==========================================================================
// Contributor Types
// ==========================================================================

export interface ContributorMetricsSummary {
  totalEntities: number;
  publishedEntities: number;
  totalInvocations: number;
  avgFitness: number;
}

export interface ContributorEntityMetric {
  entityId: string;
  name: string;
  type: string;
  status: string;
  fitnessScore: number;
  invocationCount: number;
  successRate: number;
  avgLatencyMs: number | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostUsd: number;
  lastInvokedAt: string | null;
}

export interface ContributorMetricsResult {
  summary: ContributorMetricsSummary;
  entities: ContributorEntityMetric[];
}

export interface ContributorArcheDashboardSummary {
  totalArches: number;
  publishedArches: number;
  totalInvocations: number;
  estimatedRevenueCents: number;
  avgFitness: number;
}

export interface ContributorArcheMetric {
  archId: string;
  name: string;
  simpleDescription: string;
  status: string;
  pricingModel: string;
  pricingLabel: string;
  invocationCount: number;
  successRate: number;
  fitnessScore: number;
  estimatedRevenueCents: number;
  publishedAt: string | null;
  updatedAt: string;
  editSessionId: string | null;
}

export interface ContributorArcheDashboardResult {
  summary: ContributorArcheDashboardSummary;
  arches: ContributorArcheMetric[];
}

// ==========================================================================
// Credits Types
// ==========================================================================

export interface CreditBalance {
  userId: string;
  balance: number;
  lifetimeUsed: number;
  lifetimeGranted: number;
  lastUpdated: string;
}

export interface CreditTransaction {
  transactionId: string;
  type:
    | 'grant'
    | 'usage'
    | 'refund'
    | 'adjustment'
    | 'system_grant'
    | 'purchase'
    | 'subscription_credit'
    | string;
  amount: number;
  balanceAfter: number;
  requestId: string | null;
  description: string | null;
  expenseCategory: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface CreditTransactionsResult {
  transactions: CreditTransaction[];
  total: number;
  limit: number;
  offset: number;
}

export interface SpendingCategorySummary {
  category: string;
  totalCents: number;
  count: number;
}

export interface CreditGrantResult {
  success: boolean;
  newBalance: number;
  transactionId: string;
}

// ==========================================================================
// Payment Types
// ==========================================================================

export interface PaymentConfig {
  publishableKey: string;
  purchaseAmounts: Array<{
    amountCents: number;
    displayPrice: string;
    credits: number;
  }>;
  minAmountCents: number;
  maxAmountCents: number;
  currency: string;
}

export interface CheckoutSessionResult {
  sessionId: string;
  url: string;
  publishableKey: string;
}

export interface CryptoCheckoutResult {
  chargeId: string;
  hostedUrl: string;
  expiresAt: string;
}

export interface PaymentHistoryItem {
  paymentId: string;
  amountCents: number;
  displayAmount: string;
  currency: string;
  creditsPurchased: number;
  paymentProvider: 'stripe' | 'coinbase';
  status: 'succeeded' | 'pending' | 'failed' | 'refunded' | 'partially_refunded';
  description: string | null;
  createdAt: string;
}

export interface PaymentHistoryResult {
  payments: PaymentHistoryItem[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Admin Payments Types
// ==========================================================================

export interface MaintenanceStatus {
  enabled: boolean;
  message: string | null;
  enabledAt: string | null;
  enabledBy: string | null;
}

export interface AdminPaymentsDashboard {
  period: string;
  profitAndLoss: {
    revenue: {
      totalCents: number;
      display: string;
      purchases: { totalCents: number; display: string; count: number };
      subscriptions: { totalCents: number; display: string; activeCount: number };
    };
    expense: { totalCents: number; display: string; transactionCount: number };
    systemCreditsAbsorbed: { totalCredits: number; display: string; grantCount: number };
    systemCreditUsage: {
      recipients: number;
      byGrantType: Array<{
        grantType: string;
        totalCredits: number;
        display: string;
        count: number;
      }>;
      recipientUsage: { totalConsumed: number; display: string; transactionCount: number };
      byCategory: Array<{ category: string; totalCredits: number; display: string; count: number }>;
    };
    grossMargin: { totalCents: number; display: string; percent: string; isPositive: boolean };
  };
  platform: {
    totalUsers: number;
    activeSubscribers: number;
    averageRevenuePerUser: string;
  };
  expenseByCategory: Array<{
    category: string;
    totalCredits: number;
    display: string;
    count: number;
  }>;
  topSpendingUsers: Array<{
    userId: string;
    totalSpent: number;
    displaySpent: string;
    transactionCount: number;
  }>;
}

export interface AdminPaymentsCosts {
  period: string;
  byCategory: Array<{ category: string; totalCredits: number; displayCost: string; count: number }>;
  byArche: Array<{
    archeId: string;
    archeName: string;
    totalCredits: number;
    displayCost: string;
    count: number;
  }>;
}

// ==========================================================================
// Subscription Types
// ==========================================================================

export interface SubscriptionTier {
  tier: 'free' | 'premium';
  name: string;
  priceMonthly: number;
  displayPrice: string;
  monthlyCreditAllocation: number;
  storageLimitGb: number;
  features: string[];
}

export interface SubscriptionTiersResult {
  tiers: SubscriptionTier[];
}

export interface SubscriptionInfo {
  subscriptionId: string;
  tier: 'free' | 'premium';
  tierName: string;
  status: 'active' | 'canceled' | 'past_due' | 'trialing' | 'incomplete';
  cancelAtPeriodEnd: boolean;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  monthlyCreditAllocation: number;
  storageLimitGb: number;
  features: string[];
}

// ==========================================================================
// Models Types
// ==========================================================================

export type ModelProvider = 'openai' | 'anthropic' | 'google' | 'xai';
export type ModelTier = 'fast' | 'balanced' | 'powerful';

export interface ModelCapabilities {
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  maxContextTokens: number;
  maxOutputTokens: number;
  temperatureRange: { min: number; max: number } | null;
  defaultTemperature: number;
  isReasoningModel: boolean;
  apiEndpoint: 'chat' | 'responses';
}

export interface ModelEntity {
  entityId: string;
  name: string;
  description: string;
  provider: ModelProvider;
  providerDisplayName: string;
  modelName: string;
  tier: ModelTier;
  isRecommended: boolean;
  capabilities: ModelCapabilities;
  pricing: ArchePricing;
}

export interface ProviderInfo {
  key: ModelProvider;
  displayName: string;
  models: string[];
  recommendedModelId: string;
}

export interface ModelListResponse {
  models: ModelEntity[];
  providers: ProviderInfo[];
}

// ==========================================================================
// Model Definition Types (Entity-driven parameter schema)
// ==========================================================================

/**
 * Parameter data types for model definition schema
 */
export type ParameterType = 'number' | 'enum' | 'boolean' | 'string';

/**
 * UI control types for parameter rendering
 */
export type ParameterControl = 'slider' | 'input' | 'select' | 'checkbox' | 'toggle' | 'textarea';

/**
 * Parameter categories for UI grouping
 */
export type ParameterCategory = 'output' | 'reasoning' | 'behavior' | 'advanced';

/**
 * Numeric range constraint for parameters
 */
export interface ParameterRange {
  min: number;
  max: number;
  step?: number;
  default: number;
}

/**
 * Option for enum parameters
 */
export interface ParameterOption {
  value: string;
  label: string;
  description?: string;
}

/**
 * Conditional dependency for parameter visibility
 */
export interface ParameterDependency {
  parameter: string;
  value: unknown;
}

/**
 * Conditional default values based on another parameter's value
 */
export interface ConditionalDefault {
  dependsOn: string;
  values: Record<string, unknown>;
}

/**
 * Complete parameter definition from Model Definition Entity
 *
 * Defines everything needed to:
 * 1. Validate parameter values
 * 2. Render UI controls
 * 3. Apply constraints
 * 4. Show/hide conditionally
 */
export interface ParameterDefinition {
  name: string;
  type: ParameterType;
  label: string;
  description: string;
  category: ParameterCategory;
  range?: ParameterRange;
  options?: ParameterOption[];
  defaultValue?: unknown;
  required: boolean;
  dependsOn?: ParameterDependency;
  conditionalDefault?: ConditionalDefault;
  control: ParameterControl;
  apiName?: string;
}

/**
 * API endpoint configuration for model definitions
 */
export interface ApiEndpointConfig {
  url: string;
  method: 'POST';
  format: 'chat' | 'responses' | 'completions' | 'custom';
  streaming: boolean;
}

/**
 * Model definition capabilities
 */
export interface ModelDefinitionCapabilities {
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  supportsVision: boolean;
  supportsFunctionCalling: boolean;
  supportsStreaming: boolean;
  maxContextTokens: number;
  maxOutputTokens: number;
  isReasoningModel: boolean;
  inputTokenCostPer1k?: number;
  outputTokenCostPer1k?: number;
  /** Allow provider-specific capability extensions (e.g., providerParams for image models) */
  [key: string]: unknown;
}

/**
 * Model type discriminator
 */
export type ModelType = 'text' | 'image';

/**
 * Model Definition Entity response from API
 */
export interface ModelDefinition {
  entityId: string;
  name: string;
  description: string;
  status: string;
  provider: ModelProvider;
  modelName: string;
  modelType: ModelType;
  displayName: string;
  shortDescription: string;
  apiEndpoint: ApiEndpointConfig;
  parameterSchema: ParameterDefinition[];
  executionConfig: unknown;
  capabilities: ModelDefinitionCapabilities;
}

/**
 * Model Definition list item (lighter weight for lists)
 */
export interface ModelDefinitionListItem {
  entityId: string;
  name: string;
  displayName: string;
  shortDescription: string;
  provider: ModelProvider;
  modelName: string;
  modelType: ModelType;
  tier: ModelTier;
  isRecommended: boolean;
  supportsThinking: boolean;
  maxOutputTokens: number;
  status: string;
  capabilities: ModelDefinitionCapabilities;
}

/**
 * Model Definition list response
 */
export interface ModelDefinitionListResponse {
  definitions: ModelDefinitionListItem[];
  total: number;
}

// ==========================================================================
// File Management Types
// ==========================================================================

export type FileCategory = 'general' | 'document' | 'financial' | 'media' | 'generated';
export type FileStatus = 'active' | 'processing' | 'archived' | 'deleted';
export type StorageClass = 'standard' | 'infrequent' | 'glacier';
export type StorageTier = 'free' | 'basic' | 'premium';

export interface PresignedUploadResponse {
  uploadUrl: string;
  fileId: string;
  s3Key: string;
  expiresAt: string;
}

export interface MultipartUploadResponse {
  uploadId: string;
  fileId: string;
  s3Key: string;
  partSize: number;
  totalParts: number;
}

export interface PresignedPartResponse {
  uploadUrl: string;
  partNumber: number;
  expiresAt: string;
}

export interface UserFileResponse {
  fileId: string;
  fileName: string;
  fileExtension: string | null;
  mimeType: string | null;
  sizeBytes: number;
  checksumSha256?: string | null;
  category: FileCategory;
  sensitivity?: string;
  folderId: string | null;
  tags: string[];
  metadata?: Record<string, unknown>;
  status: FileStatus;
  storageClass: StorageClass;
  sourceType?: string;
  sourceArcheId?: string | null;
  createdAt: string;
  updatedAt: string;
  accessedAt: string;
}

export interface FileListResult {
  files: UserFileResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface FolderResponse {
  folderId: string;
  name: string;
  path: string;
  parentFolderId: string | null;
  createdAt: string;
  updatedAt?: string;
}

export interface ContextFolderResponse {
  folderId: string;
  name: string;
  path: string;
  parentFolderId: string | null;
  createdAt: string;
  isProtected: boolean;
}

export interface StorageUsageBreakdown {
  category: FileCategory;
  bytes: number;
  formatted: string;
  fileCount: number;
  percentage: number;
}

export interface StorageUsageResponse {
  userId: string;
  totalBytes: number;
  totalFormatted: string;
  quotaBytes: number;
  quotaFormatted: string;
  usedPercentage: number;
  fileCount: number;
  storageTier: StorageTier;
  breakdown: StorageUsageBreakdown[];
  lastCalculatedAt: string;
}

export interface FileOperationResponse {
  operationId: string;
  fileId: string | null;
  operationType: string;
  bytesTransferred: number;
  bytesFormatted: string;
  costCredits: number;
  archeId: string | null;
  createdAt: string;
}

export interface FileOperationsListResult {
  operations: FileOperationResponse[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Image Generation Types
// ==========================================================================

export interface ImageGenerationInput {
  prompt: string;
  provider?: string;
  model?: string;
  size?: string;
  quality?: string;
  style?: string;
  providerParams?: Record<string, unknown>;
  referenceImageRef?: string;
  referenceImageFileId?: string;
  referenceStrength?: number;
}

export interface ImageGenerationResponse {
  requestId: string;
  archId: string;
  status: 'pending';
}

export interface ImageModel {
  id: string;
  name: string;
  provider: string;
  supportedSizes: string[];
  supportedQualities?: string[];
  supportedStyles?: string[];
  supportsReferenceImage?: boolean;
}

export interface ImageProviderInfo {
  key: string;
  displayName: string;
  models: string[];
  recommendedModelId: string;
}

export interface ImageModelsResponse {
  models: ImageModel[];
  providers: ImageProviderInfo[];
}

export const api = new ApiClient();
export default api;

// ==========================================================================
// Plugin Types
// ==========================================================================

export interface PluginSubmissionSummary {
  submissionId: string;
  archId: string;
  manifestName: string;
  manifestVersion: string;
  status: string;
  reviewId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PluginSubmissionListResult {
  submissions: PluginSubmissionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface PluginSubmissionDetailResult {
  submission: {
    submissionId: string;
    archId: string;
    sourceRef: string;
    manifest: Record<string, unknown>;
    status: string;
    reviewId: string | null;
    buildRef: string | null;
    errorMessage: string | null;
    createdAt: string;
    updatedAt: string;
  };
  review: {
    reviewId: string;
    status: string;
    reviewerType: string;
    riskScore: number | null;
    criticalCount: number;
    highCount: number;
    filesAnalyzed: number;
    dependenciesAnalyzed: number;
    codeFindings: unknown[] | null;
    dependencyFindings: unknown[] | null;
    policyFindings: unknown[] | null;
    qualityFindings: unknown[] | null;
    createdAt: string;
    completedAt: string | null;
  } | null;
}

// ==========================================================================
// Chat Types
// ==========================================================================

export interface ChatChannelResponse {
  channelId: string;
  channelType: 'direct' | 'global' | 'guild' | 'group';
  name: string | null;
  description: string | null;
  imageRef: string | null;
  guildId: string | null;
  createdBy: string;
  maxMembers: number | null;
  isReadOnly: boolean;
  status: 'active' | 'archived' | 'suspended';
  createdAt: string;
  updatedAt: string;
  memberCount?: number;
  unreadCount?: number;
  dmPartnerId?: string | null;
  dmPartnerDisplayName?: string | null;
  lastMessage?: {
    content: string;
    senderId: string;
    senderName: string | null;
    createdAt: string;
  } | null;
}

export interface ChatChannelMemberResponse {
  channelId: string;
  userId: string;
  role: 'owner' | 'admin' | 'moderator' | 'member';
  nickname: string | null;
  isMuted: boolean;
  lastReadAt: string | null;
  joinedAt: string;
}

export interface ChatMessageResponse {
  messageId: string;
  channelId: string;
  senderId: string;
  senderName?: string | null;
  content: string;
  messageType: 'text' | 'system' | 'attachment' | 'reply';
  replyToId: string | null;
  attachmentRefs: unknown | null;
  mentions: Array<{ userId: string; displayName: string }> | null;
  isEdited: boolean;
  editedAt: string | null;
  isDeleted: boolean;
  createdAt: string;
  reactions?: Array<{ emoji: string; count: number; userIds: string[] }>;
}

export interface ChatChannelListResult {
  channels: ChatChannelResponse[];
  total: number;
}

export interface ChatMessageListResult {
  messages: ChatMessageResponse[];
  hasMore: boolean;
  oldestMessageId: string | null;
  newestMessageId: string | null;
}

export interface ChatUnreadCount {
  channelId: string;
  unreadCount: number;
}

export interface ChatPinnedMessageResponse {
  messageId: string;
  channelId: string;
  content: string;
  senderId: string;
  senderName: string | null;
  pinnedBy: string;
  pinnedAt: string;
  createdAt: string;
}

export interface ChatBlockResponse {
  blockId: string;
  blockedId: string;
  createdAt: string;
}

export interface CreateChatChannelInput {
  channelType: 'group' | 'guild';
  name: string;
  description?: string;
  guildId?: string;
}

// ==========================================================================
// Guild Types
// ==========================================================================

export type GuildType = 'regular' | 'earning';
export type GuildLevel = 'bronze' | 'iron' | 'silver' | 'gold' | 'diamond';
export type GuildVisibility = 'public' | 'private';
export type GuildMemberRole = 'owner' | 'admin' | 'moderator' | 'member';

export interface GuildSummary {
  guildId: string;
  name: string;
  description: string | null;
  imageRef: string | null;
  guildType: GuildType;
  level: GuildLevel;
  visibility: GuildVisibility;
  ownerUsername?: string | null;
  memberCount: number;
  lifetimeEarnings: string;
  status: string;
  createdAt: string;
}

export interface GuildDetailResponse extends GuildSummary {
  ownerId: string;
  maxMembers: number | null;
  settings?: Record<string, unknown>;
  myRole?: GuildMemberRole | null;
  updatedAt: string;
}

export interface GuildListResult {
  guilds: GuildSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildMemberResponse {
  guildId: string;
  userId: string;
  username?: string | null;
  role: GuildMemberRole;
  memberSociat: string;
  joinedAt: string;
}

export interface GuildMemberListResult {
  members: GuildMemberResponse[];
  bans?: GuildBanResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildInviteResponse {
  inviteId: string;
  guildId: string;
  inviteCode: string;
  createdBy?: string;
  maxUses: number | null;
  useCount: number;
  expiresAt: string | null;
  status: string;
  createdAt: string;
}

export interface GuildInviteInfo {
  inviteCode: string;
  expiresAt: string | null;
}

export interface GuildBanResponse {
  banId: string;
  guildId: string;
  userId: string;
  username?: string | null;
  bannedBy: string;
  bannedByUsername?: string | null;
  reason: string | null;
  createdAt: string;
}

export interface GuildActivityEntry {
  activityId: string;
  guildId: string;
  userId: string;
  username?: string | null;
  activityType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface GuildActivityResult {
  activities: GuildActivityEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface GuildTreasuryResponse {
  guildId: string;
  balance: string;
  lifetimeEarned: string;
  lifetimeDistributed: string;
  distributionRule: string;
  lastDistributionAt: string | null;
  updatedAt: string;
}

export interface GuildTreasuryTransactionResponse {
  transactionId: string;
  guildId: string;
  type: string;
  amount: string;
  balanceAfter: string;
  recipientId: string | null;
  description: string | null;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface CreateGuildPayload {
  name: string;
  description?: string;
  imageRef?: string;
  guildType: GuildType;
  visibility: GuildVisibility;
}

export interface UpdateGuildPayload {
  name?: string;
  description?: string;
  imageRef?: string;
  visibility?: GuildVisibility;
  maxMembers?: number | null;
  settings?: Record<string, unknown>;
}

// ==========================================================================
// Builder Types
// ==========================================================================

export interface BuilderSessionSummary {
  sessionId: string;
  archId: string | null;
  currentName: string | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  updatedAt: string;
  totalCostCents: number;
}

export interface BuilderMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
  artifacts?: BuilderArtifact[];
}

export interface BuilderArtifact {
  type: 'ui_definition' | 'workflow' | 'entity_selection' | 'settings';
  content: unknown;
  description: string;
}

export interface BuilderSession {
  sessionId: string;
  userId: string;
  archId: string | null;
  templateId: string | null;
  conversationHistory: BuilderMessage[];
  currentName: string | null;
  currentDescription: string | null;
  currentUiDefinition: unknown | null;
  currentWorkflow: unknown | null;
  currentSettings: unknown | null;
  status: 'active' | 'paused' | 'completed' | 'abandoned';
  lastClaudeResponseAt: string | null;
  totalInputTokens: number;
  totalOutputTokens: number;
  totalCostCents: number;
  createdAt: string;
  updatedAt: string;
}

export interface BuilderTemplate {
  templateId: string;
  name: string;
  description: string | null;
  category: string | null;
  thumbnailRef: string | null;
  uiDefinition: unknown;
  workflow: unknown | null;
  defaultSettings: unknown | null;
  displayOrder: number;
  isFeatured: boolean;
}

export interface ArcheReviewSummary {
  reviewId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ReviewFindingSummary {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

export interface ReviewFinding {
  id: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  category: string;
  message: string;
  location?: string;
  suggestion?: string;
}

export interface ArcheReviewDetail {
  reviewId: string;
  archId: string;
  version: number;
  reviewerType: 'ai' | 'human';
  status: 'pending' | 'in_progress' | 'approved' | 'rejected';
  riskScore: number | null;
  securityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  policyFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  qualityFindings: {
    findings: ReviewFinding[];
    summary: ReviewFindingSummary;
  } | null;
  reviewedBy: string | null;
  humanNotes: string | null;
  reviewCostCents: number | null;
  createdAt: string;
  completedAt: string | null;
}

export interface ClaudeMessageResponse {
  message: string;
  artifacts: BuilderArtifact[];
  tokensUsed: {
    input: number;
    output: number;
  };
  suggestedNextSteps?: string[];
  creditsCost: number;
  validationWarnings?: string[];
}

// ==========================================================================
// Contact Book Types
// ==========================================================================

export interface LabeledValue {
  value: string;
  label: string;
}

export interface ContactDetail {
  id: string;
  userId: string;
  categoryId: string | null;
  priorityId: string | null;
  firstName: string;
  lastName: string;
  emails: LabeledValue[];
  phones: LabeledValue[];
  company: string | null;
  title: string | null;
  notes: string | null;
  source: string | null;
  gatheredAt: string;
  tags: string[];
  metadata: Record<string, unknown>;
  status: 'active' | 'archived' | 'deleted';
  createdAt: string;
  updatedAt: string;
  categoryName?: string | null;
  categoryColor?: string | null;
  priorityName?: string | null;
  priorityColor?: string | null;
}

export interface ContactInput {
  firstName: string;
  lastName?: string;
  emails?: LabeledValue[];
  phones?: LabeledValue[];
  company?: string | null;
  title?: string | null;
  notes?: string | null;
  source?: string | null;
  gatheredAt?: string | null;
  categoryId?: string | null;
  priorityId?: string | null;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface ContactCategory {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  contactCount: number;
}

export interface ContactCategoryInput {
  name: string;
  color: string;
}

export interface ContactPriority {
  id: string;
  name: string;
  color: string;
  sortOrder: number;
  contactCount: number;
}

export interface ContactPriorityInput {
  name: string;
  color: string;
  sortOrder?: number;
}

export interface ContactAttachment {
  id: string;
  contactId: string;
  fileId: string;
  label: string | null;
  displayOrder: number;
  createdAt: string;
  fileName?: string;
  fileSize?: number;
  mimeType?: string;
}

// ==========================================================================
// DFE Inventory Types
// ==========================================================================

export type DfeFungibility = 'fungible' | 'non_fungible';
export type DfeTransferType = 'transfer' | 'mint' | 'burn' | 'admin_grant';

export interface DfeDefinitionSummary {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  metadata: Record<string, unknown>;
  maxSupply: number | null;
  currentSupply: number;
  marketCategory: string;
  status: string;
  createdAt: string;
}

export interface DfeDefinitionDetail extends DfeDefinitionSummary {
  createdBy: string;
  updatedAt: string;
}

export interface DfeDefinitionListResult {
  definitions: DfeDefinitionSummary[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeInventoryEntry {
  itemDefId: string;
  name: string;
  description: string | null;
  fungibility: DfeFungibility;
  thumbnailUri: string | null;
  imageUri: string | null;
  definitionMetadata: Record<string, unknown>;
  category: string;
  position: string;
  balanceId: string | null;
  quantity: number | null;
  itemId: string | null;
  instanceNumber: number | null;
  itemMetadata: Record<string, unknown> | null;
  acquiredAt: string | null;
}

export interface DfeInventoryListResult {
  items: DfeInventoryEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeInventoryItemDetail {
  itemId: string;
  itemDefId: string;
  ownerId: string;
  instanceNumber: number;
  metadata: Record<string, unknown>;
  category: string;
  position: string;
  acquiredAt: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface DfeTransferEntry {
  transferId: string;
  itemDefId: string;
  itemId: string | null;
  fromUserId: string | null;
  toUserId: string | null;
  quantity: number;
  transferType: DfeTransferType;
  notes: string | null;
  createdAt: string;
}

export interface DfeTransferListResult {
  transfers: DfeTransferEntry[];
  total: number;
  limit: number;
  offset: number;
}

export interface DfeMintResult {
  success: boolean;
  items?: Array<{ itemId: string; instanceNumber: number; metadata: Record<string, unknown> }>;
  balance?: { balanceId: string; quantity: number };
}

// DFE Legacy Import types
export interface DfeLegacyImportResult {
  importId: string;
  legacyServer: string;
  legacyUsername: string;
  itemsImported: number;
  itemsSkipped: number;
  skippedItems: string[];
  importStatus: string;
  errorMessage: string | null;
}

export interface DfeLegacyImportEntry extends DfeLegacyImportResult {
  createdAt: string;
  updatedAt: string | null;
}

export interface CreateDfeDefinitionInput {
  name: string;
  description?: string;
  fungibility: string;
  thumbnailUri?: string;
  imageUri?: string;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  marketCategory?: string;
  status?: string;
}

export interface UpdateDfeDefinitionInput {
  name?: string;
  description?: string | null;
  thumbnailUri?: string | null;
  imageUri?: string | null;
  metadata?: Record<string, unknown>;
  maxSupply?: number | null;
  marketCategory?: string;
  status?: string;
}

export interface TransferDfeItemInput {
  toUserId?: string;
  toUsername?: string;
  itemDefId?: string;
  quantity?: number;
  itemId?: string;
  notes?: string;
  category?: string;
  position?: string;
}

export interface DfePositionConflict {
  category: string;
  position: string;
  items: { itemDefId: string; name: string }[];
}

// ==========================================================================
// Game Rewards Types
// ==========================================================================

export type GrSubscriptionTier = 'bronze' | 'silver' | 'gold';

export interface GrSubscriptionResponse {
  subscriptionId: string;
  tier: GrSubscriptionTier;
  startsAt: string;
  expiresAt: string;
  purchasedVia?: string;
  isActive?: boolean;
  userId?: string;
  username?: string | null;
  metadata?: Record<string, unknown>;
  createdAt?: string;
}

export interface GrTierResponse {
  tierId: string;
  tier: GrSubscriptionTier;
  displayName: string;
  currencyItemDefId: string;
  currencyItemName?: string | null;
  priceAmount: number;
  durationDays: number;
  description: string | null;
}

export interface GrTierListResult {
  tiers: GrTierResponse[];
  total: number;
}

export interface GrSubscriptionHistoryResult {
  subscriptions: GrSubscriptionResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrRewardDelivered {
  rewardType: string;
  itemDefId: string;
  rawAmount: number;
  multiplier: number;
  finalAmount: number;
}

export interface GrRewardLogEntry {
  logId: string;
  gameConfigId: string;
  gameEvent: string;
  gameScore: number;
  subscriptionTier: GrSubscriptionTier | null;
  rewardsDelivered: GrRewardDelivered[];
  createdAt: string;
  userId?: string;
  requestNonce?: string | null;
  ipAddress?: string | null;
}

export interface GrRewardLogListResult {
  entries: GrRewardLogEntry[];
  total: number;
  limit: number;
  offset: number;
}

export type GrLeaderboardType = 'high_score' | 'low_score' | 'none';

export type GrRewardMode = 'standard' | 'item_list';

export interface GrClaimCostRule {
  pattern: 'exact' | 'prefix';
  names?: string[];
  value?: string;
  currency: string;
  amount: number;
}

export interface GrGameConfigDetail {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  description: string | null;
  apiKeyPrefix: string;
  minScore: number;
  maxScore: number;
  leaderboardType: GrLeaderboardType;
  rewardMode: GrRewardMode;
  allowedItems: string[] | null;
  claimCosts: GrClaimCostRule[] | null;
  claimStateKeyword: string | null;
  allowedItemPrefixes: string[] | null;
  isActive: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface GrGameConfigCreateResult {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  apiKey: string;
  apiKeyPrefix: string;
}

export interface GrGameConfigListResult {
  gameConfigs: GrGameConfigDetail[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrStepDefinition {
  minScore: number;
  maxScore: number;
  reward: number;
}

export interface GrRewardRuleResponse {
  ruleId: string;
  gameConfigId: string;
  gameEvent: string;
  rewardType: string;
  itemDefId: string;
  m: number;
  b: number;
  maxRewardCount: number;
  bronzeMultiplier: number;
  silverMultiplier: number;
  goldMultiplier: number;
  steps: GrStepDefinition[] | null;
  baseMultiplier: number;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface GrRewardRuleListResult {
  rules: GrRewardRuleResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrRewardTypeLimitResponse {
  limitId: string;
  rewardType: string;
  minSecondsBetweenRewards: number;
  maxRewardsPerDay: number;
  createdAt?: string;
}

export interface GrRewardTypeLimitListResult {
  limits: GrRewardTypeLimitResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrTierConfigResponse {
  tierId: string;
  tier: GrSubscriptionTier;
  displayName: string;
  priceAmount: number;
  durationDays: number;
  isActive?: boolean;
  updatedAt?: string;
}

export interface GrAdminTierListResult {
  tiers: Array<GrTierResponse & { isActive: boolean; createdAt: string }>;
  total: number;
}

export interface GrAdminSubscriptionListResult {
  subscriptions: GrSubscriptionResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface GrTimePlayedAggregates {
  totalSeconds: number;
  totalSessions: number;
  games: Array<{
    gameConfigId: string;
    gameName: string;
    displayName: string;
    totalSeconds: number;
    sessionCount: number;
  }>;
}

export interface GrLeaderboardEntryResponse {
  rank: number;
  userId: string;
  username: string | null;
  bestScore: number;
  playCount: number;
  lastPlayedAt: string;
}

export interface GrLeaderboardResult {
  entries: GrLeaderboardEntryResponse[];
  total: number;
  leaderboardType: GrLeaderboardType;
  limit: number;
  offset: number;
  myRank?: { rank: number; bestScore: number; playCount: number } | null;
}

export interface GrUserBestScoresResult {
  scores: Array<{
    gameConfigId: string;
    gameName: string;
    displayName: string;
    gameEvent: string;
    bestScore: number;
    playCount: number;
    lastPlayedAt: string;
    leaderboardType: GrLeaderboardType;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface GrLeaderboardGameInfo {
  gameConfigId: string;
  gameName: string;
  displayName: string;
  leaderboardType: GrLeaderboardType;
}

// ==========================================================================
// OAuth / Connected Apps Types
// ==========================================================================

export interface OAuthAuthorizeInfo {
  app: {
    name: string;
    description: string | null;
    logoUrl: string | null;
  };
  scopes: Array<{
    scope: string;
    description: string;
  }>;
  redirectDomain: string;
  existingConsent: boolean;
}

export interface ConnectedApp {
  appId: string;
  appName: string;
  appLogoUrl: string | null;
  scopes: { scope: string; description: string }[];
  grantedAt: string;
}

// ==========================================================================
// DFE Game State Types
// ==========================================================================

export interface GameStateResponse {
  gameStateId: string;
  keyword: string;
  dataType: string;
  data?: string;
  dataBinary?: string;
  version: number;
  dataSizeBytes: number;
  checksum?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

// ==========================================================================
// Polls Types
// ==========================================================================

export interface PollQuestionPayload {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  required: boolean;
}

export interface CreatePollPayload {
  title: string;
  description?: string;
  questions: PollQuestionPayload[];
  resultsVisibility?: string;
  closesAt?: string;
}

export interface UpdatePollPayload {
  title?: string;
  description?: string | null;
  questions?: PollQuestionPayload[];
  resultsVisibility?: string;
  closesAt?: string | null;
}

export interface PollAnswerPayload {
  questionId: string;
  selectedOptions?: number[];
  textAnswer?: string;
}

export interface PollDetailResponse {
  pollId: string;
  title: string;
  description: string | null;
  questions: PollQuestionPayload[];
  resultsVisibility: string;
  status: string;
  closesAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GameStateListResult {
  states: GameStateResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface PollFullDetailResponse extends PollDetailResponse {
  createdBy: string;
  responseCount: number;
  hasResponded: boolean;
  isCreator: boolean;
}

export interface PollListResult {
  polls: Array<{
    pollId: string;
    title: string;
    status: string;
    resultsVisibility: string;
    closesAt: string | null;
    createdAt: string;
    updatedAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface GameStateAuditEntry {
  auditId: string;
  gameStateId: string;
  userId: string;
  keyword: string;
  action: string;
  oldVersion: number | null;
  newVersion: number | null;
  dataSizeBytes: number | null;
  actorId: string;
  createdAt: string;
}

export interface GameStateAuditLogResult {
  entries: GameStateAuditEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Admin User Types
// ==========================================================================

export interface InviteCodeResponse {
  code: string;
  status: 'active' | 'used' | 'expired' | 'revoked';
  createdAt: string;
  expiresAt: string | null;
  usedAt?: string;
  usedByEmail?: string;
  usedByUserId?: string;
  grantRoles: string[];
  metadata?: { description?: string; notes?: string; createdBy?: string; batchId?: string };
}

export interface AdminUserLookupResult {
  userId: string;
  username: string;
  roles: string[];
  status: string;
  createdAt: string;
}

export interface PollResponseResult {
  responseId: string;
  pollId: string;
  answers: PollAnswerPayload[];
  submittedAt: string;
}

export interface PollQuestionResultData {
  questionId: string;
  text: string;
  type: string;
  options?: string[];
  optionCounts?: number[];
  textAnswers?: string[];
  totalResponses: number;
}

export interface PollResultsResponse {
  pollId: string;
  title: string;
  totalResponses: number;
  questions: PollQuestionResultData[];
}

export interface PollPublicViewResponse {
  pollId: string;
  title: string;
  description: string | null;
  questions: PollQuestionPayload[];
  resultsVisibility: string;
  status: string;
  closesAt: string | null;
  responseCount: number;
  createdAt: string;
}

// ==========================================================================
// Fiardle Types
// ==========================================================================

export interface FiardleGuessData {
  word: string;
  result: ('green' | 'yellow' | 'gray')[];
}

export interface FiardleGameResponse {
  gameId: string;
  userId: string;
  guesses: FiardleGuessData[];
  status: 'in_progress' | 'won' | 'lost';
  answer?: string;
  completedAt: string | null;
  createdAt: string;
}

export interface FiardleGuessResponse {
  guess: FiardleGuessData;
  game: FiardleGameResponse;
}

export interface FiardleGameHistoryResponse {
  games: FiardleGameResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface FiardleStatsResponse {
  gamesPlayed: number;
  gamesWon: number;
  currentStreak: number;
  maxStreak: number;
  winRate: number;
  guessDistribution: Record<string, number>;
}

export interface FiardleLeaderboardEntry {
  userId: string;
  username: string | null;
  gamesPlayed: number;
  gamesWon: number;
  winRate: number;
  currentStreak: number;
  maxStreak: number;
}

export interface FiardleLeaderboardResponse {
  entries: FiardleLeaderboardEntry[];
  total: number;
  limit: number;
  offset: number;
}

// ==========================================================================
// Calendar Types
// ==========================================================================

export interface CalendarCollectionDetail {
  id: string;
  userId: string;
  name: string;
  description: string;
  color: string;
  timezone: string;
  isDefault: boolean;
  isVisible: boolean;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
  eventCount?: number;
}

export interface CalendarCollectionInput {
  name: string;
  description?: string;
  color?: string;
  timezone?: string;
  isDefault?: boolean;
  isVisible?: boolean;
}

export interface CalendarEventDetail {
  id: string;
  calendarId: string;
  userId: string;
  title: string;
  description: string;
  location: string;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  timezone: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  metadata: Record<string, unknown>;
  icalUid: string | null;
  originalEventId: string | null;
  createdAt: string;
  updatedAt: string;
  calendarName?: string;
  calendarColor?: string;
}

export interface CalendarEventCreateInput {
  calendarId: string;
  title: string;
  description?: string;
  location?: string;
  startAt: string;
  endAt: string;
  isAllDay?: boolean;
  timezone?: string;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  color?: string | null;
  url?: string;
  isRecurring?: boolean;
  rrule?: string | null;
  reminders?: CalendarEventReminder[];
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface CalendarEventInstance {
  id: string;
  eventId: string;
  calendarId: string;
  title: string;
  description: string;
  location: string;
  startAt: string;
  endAt: string;
  isAllDay: boolean;
  timezone: string;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  calendarName?: string;
  calendarColor?: string;
  isException: boolean;
  originalStartAt?: string;
  metadata?: Record<string, unknown>;
}

export interface CalendarEventReminder {
  type: 'notification' | 'email';
  minutesBefore: number;
}

export interface CalendarParsedImportResult {
  events: CalendarImportedEvent[];
  warnings: string[];
  errors: string[];
  calendarName?: string;
  calendarColor?: string;
  calendarTimezone?: string;
  totalParsed: number;
}

export interface CalendarImportedEvent extends CalendarEventCreateInput {
  originalSource?: string;
  exdates?: string[];
  icalUid?: string;
}

export interface CalendarImportConfirmRequest {
  calendarId: string;
  events: CalendarImportedEvent[];
  duplicateStrategy: 'skip' | 'overwrite' | 'create_new';
}

export interface CalendarImportConfirmResult {
  imported: number;
  skipped: number;
  updated: number;
  errors: string[];
}

export interface ParsedEventFields {
  title: string;
  startAt?: string;
  endAt?: string;
  isAllDay?: boolean;
  location?: string;
  description?: string;
  isRecurring?: boolean;
  rrule?: string;
  confidence: number;
}

export interface CalendarEventTemplateDetail {
  id: string;
  userId: string;
  name: string;
  title: string;
  description: string;
  location: string;
  durationMinutes: number;
  isAllDay: boolean;
  status: 'confirmed' | 'tentative' | 'cancelled';
  color: string | null;
  url: string;
  calendarId: string | null;
  isRecurring: boolean;
  rrule: string | null;
  reminders: CalendarEventReminder[];
  tags: string[];
  metadata: Record<string, unknown>;
  displayOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarEventTemplateInput {
  name: string;
  title: string;
  description?: string;
  location?: string;
  durationMinutes?: number;
  isAllDay?: boolean;
  status?: 'confirmed' | 'tentative' | 'cancelled';
  color?: string;
  url?: string;
  calendarId?: string;
  isRecurring?: boolean;
  rrule?: string;
  reminders?: CalendarEventReminder[];
  tags?: string[];
  metadata?: Record<string, unknown>;
  displayOrder?: number;
}

// ==========================================================================
// Calendar Sharing Types
// ==========================================================================

export type CalendarSharePermission = 'read' | 'write' | 'admin';

export interface CalendarShareDetail {
  id: string;
  calendarId: string;
  sharedWithUserId: string;
  sharedWithUsername?: string;
  permission: CalendarSharePermission;
  showFreeBusyOnly: boolean;
  createdAt: string;
}

export interface CalendarShareInput {
  username: string;
  permission: CalendarSharePermission;
  showFreeBusyOnly?: boolean;
}

export interface CalendarShareUpdate {
  permission?: CalendarSharePermission;
  showFreeBusyOnly?: boolean;
}

export interface SharedCalendarSummary {
  id: string;
  calendarId: string;
  calendarName: string;
  calendarColor: string;
  ownerUserId: string;
  ownerUsername: string;
  permission: CalendarSharePermission;
  showFreeBusyOnly: boolean;
}

export interface UserSearchResult {
  userId: string;
  username: string;
  displayName: string;
}

export interface BusyBlock {
  startAt: string;
  endAt: string;
}

export interface ParticipantAvailability {
  userId: string;
  username: string;
  busyBlocks: BusyBlock[];
}

export interface FreeSlot {
  startAt: string;
  endAt: string;
}

export interface FindTimeResult {
  participants: ParticipantAvailability[];
  freeSlots: FreeSlot[];
}

export interface FindTimeInput {
  participantUsernames: string[];
  rangeStart: string;
  rangeEnd: string;
  durationMinutes: number;
}

// =============================================================================
// Scheduling Links Types
// =============================================================================

export interface AvailabilityWindow {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
}

export interface SchedulingLinkDetail {
  id: string;
  userId: string;
  slug: string;
  title: string;
  description: string;
  durationMinutes: number;
  location: string;
  color: string | null;
  bufferBeforeMinutes: number;
  bufferAfterMinutes: number;
  dailyCap: number | null;
  availabilityWindows: AvailabilityWindow[];
  calendarId: string | null;
  timezone: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulingLinkInput {
  slug: string;
  title: string;
  description?: string;
  durationMinutes?: number;
  location?: string;
  color?: string;
  bufferBeforeMinutes?: number;
  bufferAfterMinutes?: number;
  dailyCap?: number | null;
  availabilityWindows?: AvailabilityWindow[];
  calendarId?: string | null;
  timezone?: string;
  isActive?: boolean;
}

export interface BookingDetail {
  id: string;
  schedulingLinkId: string;
  bookerName: string;
  bookerEmail: string;
  eventId: string | null;
  startAt: string;
  endAt: string;
  status: 'confirmed' | 'cancelled';
  notes: string;
  cancellationToken: string;
  createdAt: string;
}

export interface AvailableSlotDetail {
  startAt: string;
  endAt: string;
}

export interface PublicSchedulingInfo {
  schedulingLink: {
    slug: string;
    title: string;
    description: string;
    durationMinutes: number;
    location: string;
    color: string | null;
    timezone: string;
    availabilityWindows: AvailabilityWindow[];
  };
  availableSlots: AvailableSlotDetail[];
}

export interface BookingConfirmation {
  booking: {
    id: string;
    startAt: string;
    endAt: string;
    status: string;
    cancellationToken: string;
  };
}

// =============================================================================
// Schedulable Tasks
// =============================================================================

export interface SchedulableTaskDetail {
  id: string;
  userId: string;
  title: string;
  description: string;
  deadline: string;
  estimatedMinutes: number;
  priority: 'high' | 'medium' | 'low';
  status: 'pending' | 'scheduled' | 'completed' | 'cancelled';
  eventId: string | null;
  calendarId: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  aiSuggestions: AiScheduleSuggestion[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface SchedulableTaskInput {
  title: string;
  description?: string;
  deadline: string;
  estimatedMinutes?: number;
  priority?: 'high' | 'medium' | 'low';
  calendarId?: string;
  metadata?: Record<string, unknown>;
}

export interface AiScheduleSuggestion {
  startAt: string;
  endAt: string;
  reasoning: string;
  score: number;
}

// =============================================================================
// Focus Time
// =============================================================================

export interface FocusTimeSettingsDetail {
  id: string;
  userId: string;
  isEnabled: boolean;
  preferredTimeOfDay: 'morning' | 'afternoon' | 'evening';
  minBlockMinutes: number;
  maxBlockMinutes: number;
  daysPerWeek: number;
  preferredDays: number[];
  preferredStartHour: number;
  preferredEndHour: number;
  lookaheadWeeks: number;
  calendarId: string | null;
  color: string;
  createdAt: string;
  updatedAt: string;
}

export interface FocusTimeSettingsInput {
  isEnabled?: boolean;
  preferredTimeOfDay?: 'morning' | 'afternoon' | 'evening';
  minBlockMinutes?: number;
  maxBlockMinutes?: number;
  daysPerWeek?: number;
  preferredDays?: number[];
  preferredStartHour?: number;
  preferredEndHour?: number;
  lookaheadWeeks?: number;
  calendarId?: string | null;
  color?: string;
}

export interface CalendarSubscriptionDetail {
  id: string;
  calendarId: string;
  feedUrl: string;
  lastFetchedAt: string | null;
  refreshIntervalMinutes: number;
  status: 'active' | 'error';
  errorMessage: string | null;
  calendarName: string;
  calendarColor: string;
  eventCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CalendarAnalyticsHoursPerCalendar {
  calendarId: string;
  calendarName: string;
  calendarColor: string;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsHoursPerWeek {
  weekStart: string;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsHoursPerDayOfWeek {
  dayOfWeek: number;
  hours: number;
  eventCount: number;
}

export interface CalendarAnalyticsSummary {
  totalEvents: number;
  totalHours: number;
  avgDailyHours: number;
  avgEventDurationMinutes: number;
}

export interface CalendarAnalyticsResult {
  summary: CalendarAnalyticsSummary;
  hoursPerCalendar: CalendarAnalyticsHoursPerCalendar[];
  hoursPerWeek: CalendarAnalyticsHoursPerWeek[];
  hoursPerDayOfWeek: CalendarAnalyticsHoursPerDayOfWeek[];
}

// ==========================================================================
// Social Media Types
// ==========================================================================

export type SocialPlatform = 'twitter' | 'linkedin' | 'facebook' | 'instagram';
export type SocialAccountStatus = 'active' | 'error' | 'revoked' | 'expired';
export type SocialPostStatus =
  | 'draft'
  | 'scheduled'
  | 'publishing'
  | 'published'
  | 'failed'
  | 'cancelled';

export interface AdaptedPlatformContent {
  text: string;
  hashtags: string[];
  characterCount: number;
  suggestions: string[];
  adaptedAt: string;
}

export interface PublishAttemptResult {
  success: boolean;
  platformPostId?: string;
  url?: string;
  error?: string;
  errorCode?: string;
}

export interface SocialMediaAccountPublic {
  accountId: string;
  userId: string;
  platform: SocialPlatform;
  platformUserId: string;
  platformUsername: string | null;
  platformName: string | null;
  platformAvatarUrl: string | null;
  status: SocialAccountStatus;
  lastError: string | null;
  tokenExpiresAt: string | null;
  scopes: string[];
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaPost {
  postId: string;
  userId: string;
  originalContent: string;
  platformContent: Record<string, AdaptedPlatformContent>;
  targetAccountIds: string[];
  status: SocialPostStatus;
  publishAt: string | null;
  publishResults: Record<string, PublishAttemptResult>;
  aiSuggestions: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

export interface SocialMediaPublishLogEntry {
  logId: string;
  postId: string;
  accountId: string;
  platform: SocialPlatform;
  attemptAt: string;
  success: boolean;
  platformPostId: string | null;
  platformPostUrl: string | null;
  errorCode: string | null;
  errorMessage: string | null;
}

// ==========================================================================
// Lit Legion Types
// ==========================================================================

export interface LitLegionNpc {
  npcId: string;
  name: string;
  image: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionUserLevel {
  levelId: string;
  levelNumber: number;
  name: string;
  xpRequired: number;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionBadge {
  badgeId: string;
  name: string;
  image: string;
  description: string;
  category: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionTask {
  taskId: string;
  parentId: string | null;
  npcId: string | null;
  minLevelId: string | null;
  taskType: string;
  name: string;
  description: string;
  category: string | null;
  xpReward: number;
  maxCompletions: number | null;
  maxRetakesPerUser: number | null;
  startDate: string | null;
  endDate: string | null;
  timeIntervalMs: number | null;
  definition: Record<string, unknown> | null;
  npcTextIntro: string;
  npcTextObjectives: string;
  npcTextInProgress: string;
  npcTextSuccess: string;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionAdminTask extends LitLegionTask {
  privateDefinition: Record<string, unknown> | null;
}

export interface LitLegionTaskStatusResponse {
  taskStatusId: string;
  userId: string;
  taskId: string;
  status: string;
  completedAt: string | null;
  xpAmountReceived: number | null;
  userInput: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface LitLegionCoinReward {
  coinRewardId: string;
  taskId: string;
  coinId: string;
  coinName: string;
  quantity: number;
  createdAt: string;
}

export interface LitLegionBadgeReward {
  badgeRewardId: string;
  taskId: string;
  badgeId: string;
  createdAt: string;
}

export interface LitLegionTaskWithStatus {
  task: LitLegionTask;
  taskStatus: LitLegionTaskStatusResponse | null;
  npc: LitLegionNpc | null;
  coinRewards: LitLegionCoinReward[];
  badgeRewards: LitLegionBadgeReward[];
}

export interface LitLegionTaskStatusListResult {
  taskStatuses: LitLegionTaskWithStatus[];
  total: number;
  limit: number;
  offset: number;
}

export interface LitLegionPlayerStats {
  userId: string;
  xpAmount: number;
  level: LitLegionUserLevel;
  nextLevel: LitLegionUserLevel | null;
  tasksCompleted: number;
  tasksInProgress: number;
}

export interface LitLegionAdminTaskListResult {
  tasks: LitLegionAdminTask[];
  total: number;
  limit: number;
  offset: number;
}

export interface CreateLitLegionTaskPayload {
  name: string;
  description: string;
  taskType: string;
  xpReward: number;
  category?: string;
  npcId?: string;
  parentId?: string;
  minLevelId?: string;
  npcTextIntro?: string;
  npcTextObjectives?: string;
  npcTextInProgress?: string;
  npcTextSuccess?: string;
  coinRewards?: { coinId: string; coinName: string; quantity: number }[];
  badgeRewards?: { badgeId: string }[];
}

// ==========================================================================
// Business Types
// ==========================================================================

export interface BusinessResponse {
  businessId: string;
  userId?: string;
  businessType: string;
  name: string;
  level: number;
  status: string;
  lastCollectedAt: string | null;
  cooldownRemaining: number;
  canCollect: boolean;
  dailyCollectionsUsed: number;
  createdAt: string;
  updatedAt?: string;
}

export interface CollectRevenueResponse {
  heptalsEarned: number;
  bonusItemName: string | null;
  bonusItemCount: number;
  subscriptionTier: string;
  subscriptionMultiplier: number;
  revenueMultiplier: number;
  logId: string;
}

export interface BusinessParameterResponse {
  paramId: string;
  businessType: string;
  level: number;
  requirements: {
    jion: number;
    freshWater: number;
    cogsValue: number;
    components: Record<string, number>;
    foodValue: number;
  };
  rewards: {
    mode: string;
    heptals: number;
    bonusItem: string | null;
    bonusCount: number;
  };
  creationCost: {
    heptals: number;
    spentCoinName: string;
  };
}

export interface BusinessHistoryResponse {
  logs: Array<{
    logId: string;
    businessId: string;
    businessType: string;
    level: number;
    heptalsEarned: number;
    bonusItemName: string | null;
    bonusItemCount: number;
    subscriptionTier: string;
    subscriptionMultiplier: number;
    revenueMultiplier: number;
    cogsConsumed: Record<string, number>;
    foodsConsumed: Record<string, number>;
    materialsConsumed: Record<string, number>;
    createdAt: string;
  }>;
  total: number;
  limit: number;
  offset: number;
}

export interface BusinessStatsResponse {
  totalBusinesses: number;
  activeBusinesses: number;
  dissolvedBusinesses: number;
  businessesByType: Record<string, number>;
  totalCollections: number;
  totalHeptalsEarned: number;
}

// =============================================================================
// Beta Access Types
// =============================================================================

export interface BetaAccessSubmitInput {
  name: string;
  email: string;
  telegram?: string;
  description: string;
}

export interface BetaAccessSubmitResponse {
  message: string;
}

export interface BetaAccessRequestResponse {
  requestId: string;
  name: string;
  email: string;
  telegram: string | null;
  description: string;
  status: 'pending' | 'accepted' | 'rejected';
  accessCode: string | null;
  adminNotes: string | null;
  reviewedBy: string | null;
  reviewedAt: string | null;
  ipAddress: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BetaAccessListResponse {
  requests: BetaAccessRequestResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface BetaAccessStatsResponse {
  total: number;
  pending: number;
  accepted: number;
  rejected: number;
}

// ==========================================================================
// Learn About Fias Types
// ==========================================================================

export interface LearnAboutFiasResourceResponse {
  docId: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  sourceType: string;
  iconType: string | null;
  buttonLabel: string | null;
  fileKey: string | null;
  ragEnabled: boolean;
  totalChunks: number;
  createdAt: string;
}

export interface LearnAboutFiasResourcesResponse {
  resources: LearnAboutFiasResourceResponse[];
}

export interface LearnAboutFiasDocumentResponse {
  docId: string;
  title: string;
  description: string | null;
  sourceUrl: string | null;
  sourceType: string;
  metadata: Record<string, unknown>;
  status: string;
  totalChunks: number;
  errorMessage: string | null;
  showInResources: boolean;
  ragEnabled: boolean;
  fileKey: string | null;
  createdAt: string;
  processedAt: string | null;
}

export interface LearnAboutFiasDocumentDetailResponse extends LearnAboutFiasDocumentResponse {
  content: string | null;
}

export interface LearnAboutFiasDocumentListResponse {
  documents: LearnAboutFiasDocumentResponse[];
  total: number;
  limit: number;
  offset: number;
}

export interface LearnAboutFiasStatsResponse {
  documentCount: number;
  chunkCount: number;
  pendingDocuments: number;
  failedDocuments: number;
  avgChunksPerDocument: number;
}
